import _ from 'lodash';
import React, {cloneElement, useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useRouteMatch} from 'react-router';
import Modal from 'react-modal';
import localforage from 'localforage';
import postal from 'postal';
import Skeleton, {SkeletonTheme} from "react-loading-skeleton";

import {faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {useAppDispatch, useAppSelector} from './app/hooks';

import JobSwitchingMenu from './components/JobSwitchingMenu';
import JobSwitchingMenuContext, {JobSwitchingMenuState} from './components/jobSwitchingMenuContext';

import ApplicationContext from './ApplicationContext';

import config from './config';
import constants from './constants';
import routes from './config/routes';

import {NavigationType, openNewPage} from './util/navigationHelpers';
import { getJobNamesByCurrentUrl, getListItemByJobName, getOperationKey, MenuFilterForBillings, MenuFilterGrents, stringsEqualIgnoreCase} from './util';

import useGlobalYesNoDialog from './hooks/useGlobalYesNoDialog';
import useGlobalOkDialog from './hooks/useGlobalOkDialog';
import useSystemSettings from './hooks/useSystemSettings';

import ButtonGroup from './uiControls/ButtonGroup';
import OkButton from './uiControls/OkButton';

import IfmsTopBar from './components/IfmsTopBar';
import {IfmsConfigMenuItem} from './config/types';

import NoticeOKIcon from './img/notice_inform_blue.svg';
import NoticeYesNoIcon from './img/notice_inform_YesNo.svg';
import NoticeErrorIcon from './img/notice_error.svg';
import NoticeWarningIcon from './img/notice_warning.svg';

import {changeOperation, changeSideMenu, clearOperation} from './features/operationSlice';
import {JobMenuItem, moveJobToOperation, replaceJobMenuMetadata, replaceJobMenuParentOp} from './features/jobMenuSlice';
import { moveScreenToOperation, replaceScreenStackMetadata, replaceScreenUrlParentOpOnStack} from './features/screenStackSlice';
import {logoutUser as logout} from './features/loginSlice';
import {clearStorage} from './features/storageSlice';
import {hideGlobalOkDialog} from './features/globalOkDialogSlice';
import {hideGlobalYesNoDialog} from './features/globalYesNoDialogSlice';
import {hideGlobalYesNoCancelDialog, YesNoCancelResult} from './features/globalYesNoCancelDialogSlice';

import fastStorage from './fastStorage';
import IfmsModal from "./ui/IfmsModal";
import {JobNames, getJobNameByTbName, getJobNameFromInvBillCrdbTbName, getOpByjobName, getOpTypeFromInvBillCrdbTbName} from './framework.core/JobNames';
import {clearLastSavedJobs} from './features/lastSavedJobSlice';
import useExternalScreenFilter from './hooks/useExternalScreenFilter';
import useReportViewer from './hooks/useReportViewer';
import {ReportHyperKey} from './fms.report';
import {getListJobNameByJobName} from './components/common/listJobNames';
import { resetListScreenSettings } from './features/listScreenSettingsSlice';
import IfmsSideMenu, { IfmsSideMenuWidth, getMenuItemKey, findOpendSideMenuIndexes } from './IfmsSideMenu';
import { useMemoListOperationStorage } from './components/memoList/useMemoListOperationStorage';
import Spinner from './ui/Spinner';
import TableHeaderSizeDisplay from './uiControls/TableHeaderSizeDisplay';
import { FmsSubMenu } from './constants/fmsSubMenu';
import { getSelectedSubMenuArgs } from './components/JobListSubMenu';
import { ApiResult, InsertResultsWithData } from './framework.core/data/ApiResult';
import { getFmsGlobal } from './globals';
import useJobMenuUrlQuery from './hooks/useJobMenuUrlQuery';
import { storageKeyMap } from './hooks/useSetEditedId';

import oceanImportRoutes, { OiHblView, OiMblView } from './components/oceanImport/routes';
import oceanExportRoutes, { OeHblView, OeMblView, OeBookingView } from './components/oceanExport/routes';
import airImportRoutes, { AiHawbView, AiMawbView } from './components/airImport/routes';
import airExportRoutes, { AeHawbView, AeMawbView } from './components/airExport/routes';
import otherShipmentRoutes, { OtherShipmentView } from './components/otherShipment/routes';
import warehousingRotues, { WhReceiptView, WhReceivingView, WhShippingView } from './components/warehousing/routes';
import customerRotues, { CompanyView } from './components/common/routes';
import accountingRotues, { InvoiceView, BillView, CrdbView, GeneralInvoiceView, GeneralBillView } from './components/accounting/routes';
import brokerageTruckingRoutes, { BTLoadPlanView, BTLocalDOView, BTOrderView } from './components/brokerageTrucking/routes';
import useFunction from './hooks/useFunction';
import { SearchModelBase } from './fms.data/models/SearchModelBase';
import MyMemo from './components/reports/MyMemo';
import useJobMenus from './hooks/useJobMenus';
import LeftRightSplit from './ui/LeftRightSplit';
import { SQLField, SQLInfo, SQLType } from './framework.core/data/SQLInfo';
import { focusTabableByIndex, getCurrentTabableIndex, getTabableElementByIndex } from './util/inputUtils';
import { ActionButtonItem } from './ui/ActionButton';
import VolumebyCargoTypeYears from './components/reports/VolumebyCargoTypeYears';
import { useJournal } from './components/accounting/general/journal/JournalListView';
import HistoryView from './components/reports/HistoryView';
import PrintBill from './components/accounting/bill/report/PrintBill';
import BatchPrint from './components/reports/BatchPrint';
import { DocumentType } from "./framework.core/DocumentType";
import AutoBilling from './components/accounting/gInvoice/AutoBilling/AutoBilling';
import { CFSManifestReleaseOption } from './framework.core/edi/CFSManifestReleaseOption';
import HblReleasePopup from './components/oceanImport/hbl/HblReleasePopup';
import { ReleaseHblInfo } from './fms.data/models/ReleaseHblInfo';
import JobListViewContext from './components/common/JobListViewContext';
import { getJobFromName } from './components/common/JobListView';
import { initialSearch } from './filtering';

// Can be set to true in development mode to allow easy checking of table column sizes (button Show/Hide is displayed in bottom right corner).
const DebugTableHeaderSizes = false;

// Implementation note:
//   Try to get operation from url (if the url starts with '/oceanImport', the operation is Ocean Import)
//  Otherwise, try to use user default operation. If it is also missing, use Ocean Import operation by default
//                                            (2020 10 29) (Dario)
function operationFactory(initializationPath:string|null|undefined, userDefaultOperation:string|null|undefined = null) {
  if (!!initializationPath) {
    const operationsMap = _.map(constants.operations, (value, key) => ({
      key,
      value,
    }));

    const matchFromUrl = operationsMap.find(x => initializationPath.startsWith(`/${x.key}`));

    if (matchFromUrl) {
      return matchFromUrl.value;
    }
  }

  return userDefaultOperation ? userDefaultOperation : constants.operations.oceanImport;
}

const ModalContentStyle:React.CSSProperties = {
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  marginRight: '-50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10000
};

const ModalDialogContentStyle:React.CSSProperties = {
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  marginRight: '-50%',
  transform: 'translate(-50%, -50%)',
  border:'1px solid',
  borderRadius:'0px',
  borderColor:'#00F',
  zIndex: 10000,
  paddingLeft:'40px',
  paddingRight:'40px'
};

const ModalOverlayStyle:React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  zIndex: 999998
};

let navigationSubscription:ISubscriptionDefinition<any>|null = null;
let ajaxErrorSubscription:ISubscriptionDefinition<any>|null = null;
let ajaxStartSubscription:ISubscriptionDefinition<any>|null = null;
let ajaxEndSubscription:ISubscriptionDefinition<any>|null = null;
let loadingStartSubscription:ISubscriptionDefinition<any>|null = null;
let loadingEndSubscription:ISubscriptionDefinition<any>|null = null;
let viewReportSubscription:ISubscriptionDefinition<any>|null = null;
let tableEditSubscription:ISubscriptionDefinition<any>|null = null;
let broadcastChannel:BroadcastChannel|null = null;  //<pt220314>
let movetologinSubscription:ISubscriptionDefinition<any>|null = null; //<240221.dwshin>

let tooltipElement:HTMLElement|null|SVGElement = null;

function getTooltipPosition(tooltipNode:HTMLElement) {
  if (!tooltipElement) {
    return {left: 0, top: 0};
  }

  const rect = tooltipElement.getBoundingClientRect();
  const rectParent = (tooltipElement as HTMLElement).offsetParent;
  const tRect = tooltipNode.getBoundingClientRect();
  if (rectParent) {
    if (rectParent.getAttribute('role') === 'cell') { // Is special tooltip (INV, BIL, Consignee, Shipper...)
      tooltipElement.dataset.tooltipPosition = 'listview-cell';
    }
    else if(rectParent.className === 'ifms-job-btn-container') {
      tooltipElement.dataset.tooltipPosition = 'tab-tooltip';
    }
  }
  if (!tooltipElement.dataset.tooltipPosition || tooltipElement.dataset.tooltipPosition === 'bottom') {
    return { left: (rect.left + rect.width/2 - tRect.width), top: rect.top - rect.height };
  }
  if (tooltipElement.dataset.tooltipPosition === 'user-button') {
    return { left: rect.left, top: rect.top - 20};
  }
  if (tooltipElement.dataset.tooltipPosition === 'listview-cell') {
    return { left: (rect.left + (rectParent?.clientWidth ? rectParent?.clientWidth/2 : rectParent?.clientWidth ?? 0) ), top: rect.top - 3 };
  }
  //240805 hykim - 회장님 지시로 Column Header Tooltip 제거
  // if (tooltipElement.dataset.tooltipPosition === 'listview-header') {
  //   return { left: (rect.left + rect.width), top: rect.top - rect.height };
  // }
  if (tooltipElement.dataset.tooltipPosition === 'left') {
    return { left: rect.left - rect.width * 2, top: rect.top - rect.height };
  }
  if (tooltipElement.dataset.tooltipPosition === 'right') {
    return { left: rect.right, top: rect.top - rect.height };
  }
  if (tooltipElement.dataset.tooltipPosition === 'tab-tooltip') {
    return { left: (rect.left + rect.width - tRect.width) - 110, top: rect.top - rect.height };
  }

  return { left: (rect.left + rect.width / 2 - tRect.width / 2), top: rect.top - rect.height };
}

function createFragment(htmlStr:string) {
  const frag = document.createDocumentFragment();
  const temp = document.createElement('div');

  temp.innerHTML = htmlStr;

  while (temp.firstChild) {
    frag.appendChild(temp.firstChild);
  }

  return frag;
}

function FindOpendOperationView(currentUrl: string) {
  const resultArray: {
    isOpenOperationView: boolean,
    jobName: JobNames,
    jobParams: any
  }[] = [];

  if (currentUrl.includes(`?copyId=`)) {
    const copyIndex = currentUrl.indexOf('?copyId=');
    currentUrl = currentUrl.substring(0 , copyIndex);
  }

  //  ocean Import
  let routePath = oceanImportRoutes.find(x => x.component === OiMblView)?.path ?? '';
  let routeData  = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OIMbl, jobParams: routeData?.params });

  routePath = oceanImportRoutes.find(x => x.component === OiHblView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OIHbl, jobParams: routeData?.params });

  //  ocean Export
  routePath = oceanExportRoutes.find(x => x.component === OeMblView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OEMbl, jobParams: routeData?.params });

  routePath = oceanExportRoutes.find(x => x.component === OeHblView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OEHbl, jobParams: routeData?.params });

  routePath = oceanExportRoutes.find(x => x.component === OeBookingView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OEBook, jobParams: routeData?.params });

  // air Import
  routePath = airImportRoutes.find(x => x.component === AiMawbView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.AIMbl, jobParams: routeData?.params });

  routePath = airImportRoutes.find(x => x.component === AiHawbView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.AIHbl, jobParams: routeData?.params });

  // air Export
  routePath = airExportRoutes.find(x => x.component === AeMawbView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.AEMbl, jobParams: routeData?.params });

  routePath = airExportRoutes.find(x => x.component === AeHawbView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.AEHbl, jobParams: routeData?.params });

  // other Shipment
  routePath = otherShipmentRoutes.find(x => x.component === OtherShipmentView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.OtherOP, jobParams: routeData?.params });

  // warehousing
  routePath = warehousingRotues.find(x => x.component === WhReceiptView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.WHRcpt, jobParams: routeData?.params });

  routePath = warehousingRotues.find(x => x.component === WhReceivingView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.WHRcv, jobParams: routeData?.params });

  routePath = warehousingRotues.find(x => x.component === WhShippingView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.WHShip ,jobParams: routeData?.params });

  // customer
  routePath = customerRotues.find(x => x.component === CompanyView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.Company ,jobParams: routeData?.params });

  // accounting
  routePath = accountingRotues.find(x => x.component === InvoiceView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.Invoice ,jobParams: routeData?.params });
  
  routePath = accountingRotues.find(x => x.component ===  BillView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.Bill ,jobParams: routeData?.params });
  
  routePath = accountingRotues.find(x => x.component === CrdbView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.CrDb ,jobParams: routeData?.params });
  
  routePath = accountingRotues.find(x => x.component === GeneralInvoiceView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.GInvo ,jobParams: routeData?.params });
  
  routePath = accountingRotues.find(x => x.component === GeneralBillView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.GBill ,jobParams: routeData?.params });

  // Orchid Trans
  routePath = brokerageTruckingRoutes.find(x => x.component === BTOrderView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.BTOrder, jobParams: routeData?.params });

  routePath = brokerageTruckingRoutes.find(x => x.component === BTLoadPlanView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.BTLoadPlan, jobParams: routeData?.params });

  routePath = brokerageTruckingRoutes.find(x => x.component === BTLocalDOView)?.path ?? '';
  routeData = useRouteMatch(routePath);
  resultArray.push({ isOpenOperationView: routeData?.url === currentUrl, jobName: JobNames.BTDo, jobParams: routeData?.params });

  return resultArray.find(x => x.isOpenOperationView === true);
}

function IfmsMain() {
  const history = useHistory();
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);

  const systemSettings = useSystemSettings();

  const memoStorage = useMemoListOperationStorage();

  const [jobMenuContextState, setJobMenuContextState] = useState<JobSwitchingMenuState>({});

  const {setFilterForScreenExternally} = useExternalScreenFilter();

  const userLogin = useAppSelector(state => state.user);
  const operationState = useAppSelector(state => state.operation);
  const selectedOperation = operationState.operation;

  const globalYesNoCancelDialog = useAppSelector(state => state.globalYesNoCancelDialog);
  const globalYesNoDialog = useAppSelector(state => state.globalYesNoDialog);
  const globalOkDialog = useAppSelector(state => state.globalOkDialog);
  //<pt220928: portal>
  // const globalInputDialog = useAppSelector(state => state.globalInputDialog);

  const jobMenu = useAppSelector(x => x.jobMenu);
  const {cleanupAll, moveToSingleOpenedScreenInAnyOperationStartingWithUrl} = useJobMenus();
  const {switchJournal} = useJournal();
  
  const dispatch = useAppDispatch();

  const [focusEleAfterModalOkBtnClick, setFocusEleAfterModalOkBtnClick] = useState<HTMLElement | null>(null);

  const [apiLoading, setApiLoading] = useState(false);
  const [apiLoadingMessage, setApiLoadingMessage] = useState(false);
  const [apiLoadingWithMessage, setApiLoadingWithMessage] = useState<string|null>(null);

  const [initialized, setInitialized] = useState(false);

  const [selectedPopupMenuItem, setSelectedPopupMenuItem] = useState<IfmsConfigMenuItem|null>(null);
  const [selectedReportMenuItem, setSelectedReportMenuItem] = useState<IfmsConfigMenuItem|null>(null);
  const [selectedReportPopupMenuItem, setSelectedReportPopupMenuItem] = useState<ActionButtonItem | null>(null);

  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  const jobSwitchingTestMode = false;

  const [showGlobalDialog] = useGlobalYesNoDialog();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  // const reportRef = useRef<ReportViewerHandle>(null);

  // auto logout.
  const autoLogoutTimerId = useRef(-1);
  const autoLogoutRemainTimerId = useRef(-1);
  const isStayLoggin = useRef(false);
  const [autoLogoutCheck, setAutoLogoutCheck] = useState<boolean>(false);
  const [autoLogoutRemain, setAutoLogoutRemain] = useState(600); 

  const currentUrl = `${history.location.pathname}${history.location.search}`;
  const currentListJobName = getJobNamesByCurrentUrl(storageKeyMap, currentUrl);
  const currentListItemModel = getListItemByJobName(currentListJobName);
  const defaultUrls = ['/home/home', '/home', '/'];

  const [isHome, setIsHome] = useState(defaultUrls.includes(currentUrl));
  const isHomeRef = useRef(defaultUrls.includes(currentUrl));
  const activeUrlRef = useRef(currentUrl);
  const opendOperationData = FindOpendOperationView(currentUrl);

  const channel = postal.channel('ajax');
  const filterChannel = postal.channel('ifms-filter');
  const [showMainPop, setShowMainPop] = useState(false);
  // let storageName = '';
  // const tableElement = document.querySelectorAll('[class*=ifms-table-content]');
  // if (tableElement.length > 0) storageName = tableElement[0].id
  //const [listSelectedId] = useStorage(storageName, null);
  const listSelectedId = document.getElementById('last-selectedId')?.innerText ?? '';
  const topBarHeight = (document.getElementById('ifms-top-bar')?.offsetHeight ?? 0) - 1;
  const listData = fastStorage[`ifms-list-data-${currentUrl}`]
  const fmsGlobal = getFmsGlobal();
  const hasOpenedScreenStartingWithUrl = useJobMenuUrlQuery();

  const menus = _.cloneDeep(config.menus.find(x => x.operation === operationState.sideMenu));

  const getExternalFunctions = useFunction({
    callbacks: ['ifmsSettingsSave', 'ifmsSettingsCancel','ifmsReferenceFilesSave', 'ifmsReferenceFilesCancel']
  });

  const _isRunning = useRef<boolean>(false);
  const TaskReminderTimer = 60000;
  
  let grents = appContext.securityGrants;

  function resetJobMenuContextState() {
    setJobMenuContextState({});
  }

  function setInJobMenuContext(key: string, value: any) {
    const newJobMenuContextState = _.cloneDeep(jobMenuContextState);
    newJobMenuContextState[key] = value;  

    setJobMenuContextState(newJobMenuContextState);
  }

  const setSelectedOperation = (data:string) => {
    dispatch(changeOperation(data));
  };

  const {viewReport, viewEmptyReport} = useReportViewer();   // optab-rptviewer
  const { getSingleOpenedScreenInAnyOperationStartingWithUrl } = useJobMenus();
  
  // Report > HyperLink를 통해 동일한 항목을 반복해서 클릭하는 경우 리스트가 갱신되지 않아 항목이 사라지는 문제를 해결하기 위해 추가
  const versionCount = useRef<number>(1);   

  async function bcMessageHandler(e:any) {
    window.console.log('========================');
    window.console.log('message');
    window.console.log(e);
    window.console.log('========================');
    const args = decodeURIComponent(e.data.Target).split('^');
    let jobName, jobId, apiResult, opType, parentOp;
    window.console.log('=== args :');
    window.console.log(args);

    if (args[0] === 'CloseWindow') {
      if(window.name.indexOf('ifmssub') > -1) window.close();
      return;
    } else if (args[0] === 'MoveToLogin') {
      broadcastChannel?.postMessage({ Target: 'CloseWindow' });
      channel.publish('ajax.movetologin', {});
      return;
    }
    // else if (args[0] === 'PopupAvailCheck') {
    //   let n = window.open(window.location.origin, '_blank', `width=10,height=10,left=${window.innerWidth - 10},top=${window.innerWidth - 10}`); // show bottom right corner of the screen.
    //   if (n == null) {
    //     broadcastChannel = new BroadcastChannel('ifms-openop-channel-' + appContext.user?.suntekId.toLowerCase() + '-' + appContext.user?.id?.toLowerCase());
    //     broadcastChannel.postMessage({Target:'ShowAlertMsg', Msg:'Please allow popups for this website.'});
    //   } else {
    //     n.close();
    //   }
    //   return;
    // }
    else if(args[0] === 'OpenOperationDetail') {
      switch(args[1].toUpperCase()) {
        case 'OIM': 
          jobName = JobNames.OIMbl; jobId = args[2]; opType = constants.opTypes.oceanImport; break;
        case 'OIH': 
          jobName = JobNames.OIHbl; jobId = args[2]; opType = constants.opTypes.oceanImport; break;
        case 'OOM': 
        case 'OEM': 
          jobName = JobNames.OEMbl; jobId = args[2]; opType = constants.opTypes.oceanExport; break;
        case 'OOH': 
        case 'OEH': 
          jobName = JobNames.OEHbl; jobId = args[2]; opType = constants.opTypes.oceanExport; break;
        case 'AIM': 
          jobName = JobNames.AIMbl; jobId = args[2]; opType = constants.opTypes.airImport; break;
        case 'AIH': 
          jobName = JobNames.AIHbl; jobId = args[2]; opType = constants.opTypes.airImport; break;
        case "AOM": 
        case "AEM": 
          jobName = JobNames.AEMbl; jobId = args[2]; opType = constants.opTypes.airExport; break;
        case "AOH": 
        case "AEH": 
          jobName = JobNames.AEHbl; jobId = args[2]; opType = constants.opTypes.airExport; break;
        case "OTH": 
          jobName = JobNames.OtherOP; jobId = args[2]; opType = constants.opTypes.otherShipment; break;
        case "WHR": 
          jobName = JobNames.WHRcv; jobId = args[2]; opType = constants.opTypes.warehousing; break;
        case "WHS": 
          jobName = JobNames.WHShip; jobId = args[2]; opType = constants.opTypes.warehousing; break;
        case "ODM": 
          jobName = JobNames.BTOrder; jobId = args[2]; opType = constants.opTypes.order; break;   
        case "ODL": 
          jobName = JobNames.BTLoadPlan; jobId = args[2]; opType = constants.opTypes.load; break;   
        case "T_INVOHD":
          apiResult = await appContext.api.getSourceJobInfo(JobNames.Invoice, args[2]);
          window.console.log("============== getSourceJobInfo apiResult:");
          window.console.log(apiResult);
          if(apiResult && apiResult.success && apiResult.result) {
            // jobId = args[2];
            // if(apiResult.result.tbName.toUpperCase() === "T_STORAGE") {
            //   jobName = JobNames.WHStorageInv;
            //   opType = constants.opTypes.warehousing;
            // } else {
            //   jobName = JobNames.Invoice;
            //   if(apiResult.result.tbId === 0) {
            //     jobName = JobNames.GInvo;
            //   }
            //   opType = constants.opTypes.accounting;  
            // }

            jobId = args[2];
            jobName = JobNames.Invoice;
            switch(apiResult.result.tbName.toUpperCase()) {
              case "T_STORAGE":
                jobName = JobNames.WHStorageInv;
                opType = constants.opTypes.warehousing;
                break;
              case "T_WRECEIVE":
              case "T_WRECEIVEHD":
              case "T_WSHIPHD":
              case "T_WHPERIOD":
                opType = constants.opTypes.warehousing;
                break;
              default:
                if (apiResult.result.tbId === 0) {
                  jobName = JobNames.GInvo;
                }
                opType = constants.opTypes.accounting;  
                break;
            }
          }
          break;

        case "T_APHD":
          apiResult = await appContext.api.getSourceJobInfo(JobNames.Bill, args[2]);
          if (apiResult && apiResult.success && apiResult.result) {
            // jobName = JobNames.Bill; jobId = args[2];
            // if(apiResult.result.tbName.toUpperCase() === '') {
            //     if(apiResult.result.tbId === 0) {
            //       jobName = JobNames.GBill; jobId = args[2];
            //     } 
            // }
            // opType = constants.opTypes.accounting; 

            jobId = args[2];
            jobName = JobNames.Bill;
            switch(apiResult.result.tbName.toUpperCase()) {
              case "T_WRECEIVE":
              case "T_WRECEIVEHD":
              case "T_WSHIPHD":
                opType = constants.opTypes.warehousing;
                break;
              default:
                if (apiResult.result.tbId === 0) {
                  jobName = JobNames.GBill;
                }
                opType = constants.opTypes.accounting;  
                break;
            }
          }
          break;
        case "T_CRDBHD": 
          // jobName = JobNames.CrDb; jobId = args[2]; opType = constants.opTypes.accounting; 
          apiResult = await appContext.api.getSourceJobInfo(JobNames.CrDb, args[2]);

          if (apiResult && apiResult.success && apiResult.result) {
            jobId = args[2];
            jobName = JobNames.CrDb;
            switch(apiResult.result.tbName.toUpperCase()) {
              case "T_WRECEIVE":
              case "T_WRECEIVEHD":
              case "T_WSHIPHD":
              case "T_WHPERIOD":
                opType = constants.opTypes.warehousing;
                break;
              default:
                opType = constants.opTypes.accounting;  
                break;
            }
          }
          break;
        case "T_DEPOHD":
          jobId = args[2];
          if (args[4] === "D") {
              jobName = JobNames.CustPayment;
          }
          else if (args[4] === "M") {
              jobName = JobNames.CCPayment;
          }
          else {
              jobName = JobNames.BillPayment; 
          }
          opType = constants.opTypes.accounting; 
          break;
        case "PAY": jobName = JobNames.Payroll2; jobId = args[2]; opType = constants.opTypes.accounting; break;
      }

      //window.console.log(`jobName=${jobName}, jobId=${jobId}`);
      if (jobName) {
        // <dario> - Example how to open list screen with filtering. This example opens invoice list view
        //const invoiceNo = 'INV-18931';
        let listUrl = `/common/joblistview/${getListJobNameByJobName(jobName)}/${opType}`;

        // find all screen stack - url screen opened.
        const lastScreenQuery = getSingleOpenedScreenInAnyOperationStartingWithUrl(listUrl);
        let lastScreen;
        if (lastScreenQuery && lastScreenQuery.item) {
          lastScreen = lastScreenQuery.item;
          listUrl = lastScreen.url;
          parentOp = listUrl?.split("parentOp=")[1];
        } else if (
          (args[1].toUpperCase() === "T_INVOHD" ||
            args[1].toUpperCase() === "T_APHD" ||
            args[1].toUpperCase() === "T_CRDBHD") && opType !== constants.opTypes.warehousing
        ) {
          listUrl = `${listUrl}?parentOp=billings`
        }

        setFilterForScreenExternally(
          listUrl,
          'id', // filter property name in model
          '', // property label, can be omitted as ''
          jobId?.toString() ?? '', // Filter value
          'u2Date', // order by column in model
          false,    // orderByAscending
          true,     // startWithEntry
          ({ ...new SearchModelBase(), version: versionCount.current } as SearchModelBase)  // Update version
        );
        versionCount.current += 1;

        if (lastScreen) {
          openNewPage(history, listUrl, NavigationType.jobMenuSwitch, lastScreen.metadata, parentOp);
        } else {
          openNewPage(history, listUrl, NavigationType.newPage);
        }
      } else {
        //Detail View OPen
        let link = '';
        if (args[1].toUpperCase() === 'CUSTOMERPAY') {
          link = `/accounting/acCustomerPayment/`;
        }
        else if (args[1].toUpperCase() === 'BILLPAY') {
          link = `/accounting/acBillPayment/`;
        }
        else if (args[1].toUpperCase() === 'AGENTSUMMARY') {
          if (+args[3] < 0) {
            link = `/accounting/acBillPayment/`;
          } else {
            link = `/accounting/acCustomerPayment/`;
          }
        }

        if (hasOpenedScreenStartingWithUrl(link)) {
          await showGlobalOkDialog(<p>Payment Operation is already in progress</p>);
          return;
        }else
          openNewPage(history, `${link}add/${args[2]}`, NavigationType.newPage);
      }
    } else if (args[0] === 'CompanySalesSummaryLink') {
      let model = new SearchModelBase();
      jobName = JobNames.OIMbl;

      switch (args[1]) {
        case "T_OIMMAIN":
          jobName = JobNames.OIMbl;
          opType = constants.opTypes.oceanImport;
          break;
        case "T_OIHMAIN":
          jobName = JobNames.OIHbl;
          opType = constants.opTypes.oceanImport;
          break;
        case "T_OOMMAIN":
          jobName = JobNames.OEMbl;
          opType = constants.opTypes.oceanExport;
          break;
        case "T_OOHMAIN":
          jobName = JobNames.OEHbl;
          opType = constants.opTypes.oceanExport;
          break;
        case "T_AIMMAIN":
          jobName = JobNames.AIMbl;
          opType = constants.opTypes.airImport;
          break;
        case "T_AIHMAIN":
          jobName = JobNames.AIHbl;
          opType = constants.opTypes.airImport;
          break;
        case "T_AOMMAIN":
          jobName = JobNames.AEMbl;
          opType = constants.opTypes.airExport;
          break;
        case "T_AOHMAIN":
          jobName = JobNames.AEHbl;
          opType = constants.opTypes.airExport;
          break;
        case "T_GENMAIN":
          jobName = JobNames.OtherOP;
          opType = constants.opTypes.otherShipment;
          break;
        case "T_WRECEIVE":
          jobName = JobNames.WHRcv;
          opType = constants.opTypes.warehousing;
          break;
        case "T_WSHIPHD":
          jobName = JobNames.WHShip;
          opType = constants.opTypes.warehousing;
          break;
        case "T_WHPERIODINV":
          jobName = JobNames.Invoice;
          opType = constants.opTypes.warehousing;
          break;
        case "T_WHPERIODCRDB":
          jobName = JobNames.CrDb;
          opType = constants.opTypes.warehousing;
          break;
        case "T_STORAGE":
          jobName = JobNames.WHStorageInv;
          opType = constants.opTypes.warehousing;
          break;
        case "T_BTORDER":
          jobName = JobNames.BTOrder;
          opType = constants.opTypes.order;
          break;
        case "T_BTLOAD":
          jobName = JobNames.BTLoadPlan;
          opType = constants.opTypes.load;
          break;
      }

      // switch (args[2]) {
      //   case "Customer":
      //     model.recognized = "R";
      //     model.customer = parseInt(args[3]);
      //     model.fromConfirmDate = args[4];
      //     model.toConfirmDate = args[5];
      //     model.shipmentType = args[6];
      //     model.searchField.push("customer");
      //     break;

      //   case "Agent":
      //     model.recognized = "R";
      //     model.agent = parseInt(args[3]);
      //     model.fromConfirmDate = args[4];
      //     model.toConfirmDate = args[5];
      //     model.shipmentType = args[6];
      //     model.searchField.push("agent");
      //     break;
      // }

      if (model != null) {
        // model.searchField.push("recognized");
        // model.searchField.push("confirmDate");
        // model.searchField.push("shipmentType");
        model.extraWhere = `Id in (${args[2]})`
        model.version = versionCount.current;
        model.isSearchByReport = true;
        const listUrl = `/common/joblistview/${getListJobNameByJobName(jobName)}/${opType}`;

        setFilterForScreenExternally(
          listUrl,
          '', // filter property name in model
          '', // property label, can be omitted as ''
          'B/L G. Profit', // Filter value
          '', // order by column in model
          false,    // orderByAscending
          true,     // startWithEntry
          model  // Update version
        );
        versionCount.current += 1;
        openNewPage(history, listUrl, NavigationType.newPage);
      }
    // } else if (args[0] === 'CompanySalesDetailLink') {
    //   let model = new SearchModelBase();
    //   let blType = '';

    //   switch (args[1]) {
    //     case "T_INVOHD":
    //       if (args[2] == "T_STORAGE") {
    //         blType = 'whStorageInvoice';
    //         opType = constants.opTypes.warehousing;
    //       } else if (args[2] == "T_WHPERIODINV") {
    //         blType = 'invoice';
    //         opType = constants.opTypes.warehousing;
    //       } else {
    //         blType = 'invoice';
    //         parentOp = true;
    //       }
    //       break;    
    //     case "T_APHD":
    //       blType = 'bill';
    //       parentOp = true;
    //       break;
    //     case "T_CRDBHD":
    //       if (args[2] == "T_WHPERIODCRDB") {
    //         blType = 'crdb';
    //         opType = constants.opTypes.warehousing;
    //       } else {
    //         blType = 'crdb';
    //         parentOp = true;
    //       }
    //       break;
    //   }

    //   if (model != null) {
    //     model.extraWhere = `Id in (${args[3]})`
    //     model.version = versionCount.current;
    //     model.isSearchByReport = true;
    //     const listUrl = `/common/joblistview/${blType}/${opType ? opType : 'accounting'}${parentOp ? '?parentOp=billings': ''}`;

    //     setFilterForScreenExternally(
    //       listUrl,
    //       '',
    //       '',
    //       '',
    //       '',
    //       false,
    //       true,
    //       model
    //     );
    //     versionCount.current += 1;
    //     openNewPage(history, listUrl, NavigationType.newPage);
    //   }
    } else if (args[0] === 'CargoTypeYears') {
      let jobName = JobNames.None;

      switch (args[1]) {
        case "Ocean Import":
          jobName = JobNames.OIMbl;
          break;
        case "Ocean Export":
          jobName = JobNames.OEMbl;
          break;
        case "Air Import":
          jobName = JobNames.AIMbl;
          break;
        case "Air Export":
          jobName = JobNames.AEMbl;
          break;
      }

      setSelectedReportPopupMenuItem(null);

      setSelectedReportPopupMenuItem({
        label: args[1],
        popupWidth: 725,
        popupComponent:
          <VolumebyCargoTypeYears
            onPopupClose={() => setSelectedReportPopupMenuItem(null)}
            jobName={jobName}
            toDate={args[2]}
            unit={args[3]}
          />
      });
    }
    else {
      //optab-rptviewer
      //openNewPage(history, `/common/report/${e.data.Target}`, NavigationType.newPage);
      const reportId = decodeURIComponent(e.data.Target);
      const hlinkData = reportId.split('^');
      if([ReportHyperKey.DutyPendings, ReportHyperKey.NewProfitDetail1, ReportHyperKey.NewProfitDetail2, ReportHyperKey.NewProfitDetail2GP, ReportHyperKey.EmpPerformanceDetail].indexOf(hlinkData[0] as ReportHyperKey) >= 0) {
        // Find last operation before selecting report menu
        opType = _.findKey(constants.operations, x => x === selectedOperation)??'';
      } else {
        opType = constants.opTypes.accounting;
      }
      if (hlinkData[0].substring(0, 4).toUpperCase() === "HTTP")
        window.open(hlinkData[0]);
      else
        viewReport(reportId, opType, true, true);
    }
  }

  async function getReminderTime(){  
    const resultTime = await appContext.api.getReminderTaskTime(appContext.user ? appContext.user.id ?? '' : '');
    let tempInfoContent = appContext.appInfoAreaContent;
    if(!!resultTime){ //setSystemSettings가 바로 갱신이 안되어 강제로 값 변경
      if(appContext.systemSettings){
        appContext.systemSettings.reminderStatus = resultTime[0] ?? '';
        appContext.systemSettings.reminderTime = resultTime[1] ?? '';
        appContext.systemSettings.reminderDueTime = resultTime[2] ?? '';
      }
      //IfmsTopBar > Reminder Icon이 바로 갱신되지 않아 강제로 값 변경
      appContext.setSystemSettings({
        reminderStatus: resultTime[0] ?? '',
        reminderTime : resultTime[1] ?? '',
        reminderDueTime: resultTime[2] ?? '',
      });
    }
    appContext.setAppInfoAreaContent(tempInfoContent);
  }

  function stayLoggedin(){
    appContext.logoutWorker?.postMessage(['clearTimeout', autoLogoutRemainTimerId.current]);

    appContext.logoutWorker?.postMessage(['getAutoLogoutRemainInit']);
    appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current]);
    setAutoLogoutCheck(false);
  }

  useEffect(() => {
    if (autoLogoutRemain <= 0) {
      if(!isStayLoggin.current)
        timeLogout();
      else 
        isStayLoggin.current = false;
    }
  }, [autoLogoutRemain]);

  async function timeLogout() {
    console.log(`timeout. clear.`);
    appContext.logoutWorker?.postMessage(['clearTimeout', autoLogoutTimerId.current]);
    setAutoLogoutCheck(false);

    console.log(`timeout. logout.`);
    await logoutUser(false);
  }

  
  useEffect(() => {
    if (!['referenceFiles', 'settings', 'BusinessIntelligence'].some(x => currentUrl.endsWith(x))) {
      const selectedSideMenuClassName = !appContext.useDarkMode ? 'ifms-side-menu-item-selected-onclick' : 'ifms-side-menu-item-selected-onclick-darkmode'; 
      const extendedSubMenuClassName = !appContext.useDarkMode ? 'ifms-sub-menu-item-withscroll' :  'ifms-sub-menu-item-withscroll-darkmode' ;
      const selectedSubMenuClassName = !appContext.useDarkMode ? 'ifms-sub-menu-item-selected-onclick' :  'ifms-sub-menu-item-selected-onclick-darkmode' ;
      const sideMenuClassName = !appContext.useDarkMode ? 'ifms-side-menu-item-withscroll' :  'ifms-side-menu-item-withscroll-darkmode' ;

      //clear side menu background color 
      const alreadySideMenuOpend = document.querySelectorAll(`.${selectedSideMenuClassName}`);
      for (let i = 0; i < alreadySideMenuOpend.length; ++i) {
        alreadySideMenuOpend[i].className = sideMenuClassName;
      }
      const alreadySubMenuOpend = document.querySelectorAll(`.${selectedSubMenuClassName}`);
      for (let i = 0; i < alreadySubMenuOpend.length; ++i) {
        const parentIndex = alreadySubMenuOpend[i].className.replace(/[^0-9]/g, "");
        alreadySubMenuOpend[i].className = `${extendedSubMenuClassName} parent-index${parentIndex}`;
      }

      //Set side menu background color
      const findOpendSideMenuIndex = menus?.job.findIndex(x => x.link?.includes(currentUrl)) ?? -1;
      if (findOpendSideMenuIndex !== -1) {
        const menuItemKey = getMenuItemKey(operationState.sideMenu, 'ifms-side-menu', findOpendSideMenuIndex);
        const findOpendSideMenu = document.getElementById(menuItemKey);
        if (findOpendSideMenu){
          findOpendSideMenu.className = selectedSideMenuClassName;
          // const imgElements = findOpendSideMenu.querySelectorAll('svg');
          // imgElements.forEach(img => img.remove());
        }
      }
      else {
        const opendParentIndexes = findOpendSideMenuIndexes();
        let setBackGroundSubMenuIndex = -1;

        if (opendParentIndexes.length > 0) {
          for (let index = 0; index < opendParentIndexes.length; index++) {
            const parentIndex = Number(String(opendParentIndexes[index]).replace(/[^0-9]/g, ""));

            if (menus?.job[parentIndex]) {
              setBackGroundSubMenuIndex = menus?.job[parentIndex].items?.findIndex(x => x.link && currentUrl.includes(x.link)) ?? -1;
            }
            
            if (setBackGroundSubMenuIndex !== -1) {
              const menuItemKey = getMenuItemKey(`${operationState.sideMenu}`, `ifms-side-menu-${parentIndex}`, setBackGroundSubMenuIndex);
              const findOpendSubMenu = document.getElementById(menuItemKey);
              if (findOpendSubMenu)
                findOpendSubMenu.className = `${selectedSubMenuClassName} parent-index${parentIndex}`;
              break;
            }
          }
        }
        else {
          const moreMenuIndex = menus?.job.findIndex(x => x.text === 'More Menu');
          if (moreMenuIndex !== undefined && moreMenuIndex !== -1) {
            setBackGroundSubMenuIndex = menus?.job[moreMenuIndex]?.items?.findIndex(x => x.link && currentUrl.includes(x.link.split('?')[0])) ?? -1;
            if (setBackGroundSubMenuIndex !== -1) {
              const menuItemKey = getMenuItemKey(`${operationState.sideMenu}`, `ifms-side-menu-${moreMenuIndex}`, setBackGroundSubMenuIndex);
              const findOpendSubMenu = document.getElementById(menuItemKey);
              if (findOpendSubMenu)
                findOpendSubMenu.className = `${selectedSubMenuClassName} parent-index${moreMenuIndex}`;
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus, operationState.sideMenu, appContext.useDarkMode])

  useEffect(() => {

    // To clear old localforage storage, collect by 
    const clearLocalStorage = async() => {
      const keys = await localforage.keys();
      // tableName: [key1, key2]
      const listKeys = _.groupBy(keys, (k) => k.split('tbl-list')[1]);
      _.keys(listKeys).forEach((p) => {
        let maxKey = _.maxBy(listKeys[p], (k) => k.split('-tbl-list')[0].replace('table-v-', '').replace('-', ''));
        // remove except maxKey
        listKeys[p].forEach((k) => {
          if(k !== maxKey)
            localforage.removeItem(k);
        });
      });
    }    
    clearLocalStorage();
    //

    subscribeChannels();

    //<pt220314: Please remember that BroadcastChannel works only when publisher and subscriber 
    //  must be open under the same host:port and same proptocol. So, under development environment
    //  yarn build and copy build folder to iFms.Api project's frontend\build folder:
    //  "PS E:\Projects\ifms.web-proto\frontend-typescript> xcopy /s /r /y .\build\*.* ..\api\src\iFms.Api\frontend\build\"
    //  and run VS2022.
    //  Open browser independently with url http://localhost:5000/index.html, then login page appears.
    //  Start debugging here.
    broadcastChannel = new BroadcastChannel('ifms-openop-channel-' + appContext.user?.suntekId.toLowerCase() + '-' + appContext.user?.id?.toLowerCase());
    broadcastChannel.addEventListener('message', bcMessageHandler);
    //pt220314>

    // Back handler - don't allow it. Depend on the double push logic inside navigation helpers
    window.addEventListener("popstate", e => {
      history.go(1);
    });

    // const newOperation = operationFactory(history.location.pathname, userLogin.user?.defaultOperation);
    const newOperation = 'Home';

    if (selectedOperation !== newOperation) {
      setSelectedOperation(operationFactory(newOperation));
    }

    window.addEventListener('mousemove', tooltipMonitor);

    if (appContext.logoutWorker) {
      appContext.logoutWorker.onmessage = function (event) {
        //console.log('logoutWorker.onmessage:', event.data);

        switch (event.data[0]) {
          case 'fireLogoutReaminTimer':
            //console.log('logoutWorker.onmessage:', event.data);
            setAutoLogoutCheck(true);

            appContext.logoutWorker?.postMessage(['getAutoLogoutRemainInit']);
            appContext.logoutWorker?.postMessage(['createInterval']);
            break;
          case 'setTimerId':
            //console.log('logoutWorker.onmessage:', event.data);
            appContext.logoutWorker?.postMessage(['clearTimeout', autoLogoutTimerId.current]);
            autoLogoutTimerId.current = event.data[1];
            break;
          case 'setAutoLogoutRemain':
            setAutoLogoutRemain(event.data[1]);
            break;
          case 'setRemainTimerId':
            //console.log('logoutWorker.onmessage:', event.data);
            autoLogoutRemainTimerId.current = event.data[1];
            break;
        }
      };
    }

    window.addEventListener('keydown', (e) => {
      globalDirtyMarkCleaner(e);
      appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current]);
    });
    window.addEventListener('click', (e) => {
      appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current]);
    });
    window.addEventListener('unload', (e) => {
      if (window.name.indexOf('ifmssub') === -1) broadcastChannel?.postMessage({Target:'CloseWindow'});
    });

    if (!initialized) {  //fire once.
      appContext.logoutWorker?.postMessage(['getAutoLogoutRemainInit']);
      appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current]);
    }

    return unsubscribeAll;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

 
  function useInterval(callback:() => void, delay: any) {
    const savedCallback = useRef<(() => void) | null>(null);
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        function tick() {
          if (savedCallback.current) {
            savedCallback.current();
          }
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

  useEffect(() => {
    if (selectedOperation === null) {
      // Initial setup
      // setInitialized(true);
      return;
    }

    if (!initialized) {
      setInitialized(true);
      redirectToUserDefaultUrl();
      return;
    }

    // const currentUrl = `${history.location.pathname}${history.location.search}`;
    // const operationKey = getOperationKey(selectedOperation);

    // if (!operationKey) {
    //   return;
    // }

    // let currentScreenUrlForOperation = _.last(screenStack[operationKey]);

    // if(history.location.search?.indexOf('parentOp') > -1){
    //   currentScreenUrlForOperation = _.last([...screenStack[operationKey], 
    //     ...screenStack.accounting, 
    //     ...screenStack.oceanExport, 
    //     ...screenStack.airExport]);
    // }

    // if (currentScreenUrlForOperation && !stringsEqualIgnoreCase(currentUrl, currentScreenUrlForOperation.url)) {
    //   openNewPage(history, currentScreenUrlForOperation.url, NavigationType.newPage);
    // } 
    // else {
    //   const specialOperations = ['Other Shipment', 'Customer List', 'Standalone'];
    //   if (specialOperations.every(x => x !== selectedOperation)) {
    //     // Show operation home scren
    //     openNewPage(history, '/home/home', NavigationType.newPage);
    //   }
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOperation]);

  useInterval(() => {
    appContext.setUseCheckReminder(!appContext.useCheckReminder);
  },_isRunning.current ? TaskReminderTimer: null);

  useEffect(() => {
    const getTime = async () => {
      appContext.setAppInfoAreaContent(appContext.appInfoAreaContent);
      await getReminderTime();

      if(appContext.systemSettings?.reminderStatus === 'R'){
        setShowMainPop(true);
      }
      if(appContext.systemSettings?.reminderDueTime !== ''){
        _isRunning.current = true;
      } else {
        _isRunning.current = false;
      }
    };
    getTime();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [appContext.useCheckReminder ]);

  useEffect(() => {
    if (!showMainPop) {
      setTimeout(() => {
        if(appContext.myMemoTaskReminder){
          const editButton = document.getElementById('memo-log-edit-button');
          if (editButton) {
            editButton.click();
          }
        }
      }, 2000);
    }
  }, [showMainPop]);

  useEffect(() => {
    if (focusEleAfterModalOkBtnClick) {
      setTimeout(() => {
        if (getCurrentTabableIndex(document.activeElement) < 0) {
          (focusEleAfterModalOkBtnClick as HTMLElement).focus()
        }
        setFocusEleAfterModalOkBtnClick(null);
      }, 200);
    }
  }, [focusEleAfterModalOkBtnClick])

  //operation side menu
  if (menus && menus.job) {
    // securityTaskCode filter
    menus.job = MenuFilterGrents(menus.job, grents, appContext);

    if(menus.download) {
      // securityTaskCode filter
      menus.download = MenuFilterGrents(menus.download, grents, appContext);

      // const ediIndex = menus.download.findIndex(x => x.text === 'EDI');
      // if (ediIndex >= 0 && !appContext.suntekSettings?.edi?.ediForFS3) {
      //   if (menus.download[ediIndex].items) {
      //     menus.download[ediIndex].items = menus.download[ediIndex].items?.filter(x => x.text !== 'FS3.0 EDI');
      //   }
      // }

      // if (ediIndex >= 0 && !appContext.suntekSettings?.edi?.ediblSuntekCustoms) {
      //   if (menus.download[ediIndex].items) {
      //     menus.download[ediIndex].items = menus.download[ediIndex].items?.filter(x => x.text !== 'B/L from Oversea Partner');
      //   }
      // }

      // if ((menus.download[ediIndex]?.items?? []).length <= 0) {
      //   menus.download.splice(ediIndex, 1);
      // }

      const ediForFS3Index = menus.download.findIndex(x => x.text === 'FS3.0 EDI');
      if (ediForFS3Index >= 0 && !appContext.suntekSettings?.edi?.ediForFS3)
        menus.download.splice(ediForFS3Index, 1);

      const ediblSuntekCustomsIndex = menus.download.findIndex(x => x.text === 'B/L from Oversea Partner');
      if (ediblSuntekCustomsIndex >= 0 && !appContext.suntekSettings?.edi?.ediblSuntekCustoms)
        menus.download.splice(ediblSuntekCustomsIndex, 1);
    }

    const moreMenuInjectBottom = menus?.moreMenuLocation === 'bottom';
    if (menus.moreMenu) {
      let moreMenuList = menus.moreMenu.filter(x => CheckMoreMenuJobName(x.moreMenuJobName) || (moreMenuInjectBottom && x.level == 0) );
      moreMenuList?.forEach(x => x.metadata = {
        ...x.metadata,
        operationMode: opendOperationData?.jobParams?.mode
      })
      if (!moreMenuInjectBottom) {
        if(menus?.operation !== 'Manifest' && menus?.operation !== 'Load'){
          moreMenuList?.sort((a, b) => {
            const textA = String(a.text).toUpperCase();
            const textB = String(b.text).toUpperCase();
            return textA.localeCompare(textB);
          });
        }
      }

      if (moreMenuList && moreMenuList.length > 0) {  
        switch (menus?.operation) {
          case 'Ocean Import':
            if (CheckMoreMenuJobName(JobNames.OIMbl)) {
              const menuIndex = moreMenuList.findIndex(x => x.text === 'CFS Manifest');
              if (!appContext.suntekSettings?.edi?.ediForStGeorgeWarehouse)
                moreMenuList.splice(menuIndex, 1);
            }
            else if (currentUrl === storageKeyMap.get(JobNames.OIHbl) || history.location.search.includes(`jobname=${JobNames.OIHbl}`)) {
              let menuIndex = moreMenuList.findIndex(x => x.text === 'CFS Release');
              if (!appContext.suntekSettings?.edi?.ediForStGeorgeWarehouse)
                moreMenuList.splice(menuIndex, 1);

              menuIndex = moreMenuList.findIndex(x => x.text === 'Arrival Notice' && x.link !== null);
              if (!systemSettings?.bl?.changeTitleInAnMenu)
                moreMenuList.splice(menuIndex, 1);
              else
                moreMenuList.splice(menuIndex + 1, 1);
            }
            break;
          case 'Ocean Export':
            if (currentUrl === storageKeyMap.get(JobNames.OEMbl) || history.location.search.includes(`jobname=${JobNames.OEMbl}`)) {
              // <250205.dwshin. releated pims. remove menu.>
              // let menuIndex = moreMenuList.findIndex(x => x.text === 'Carrier Shipping Instruction');
              // if (!appContext.suntekSettings?.edi?.ediForCarrier)
              //   moreMenuList.splice(menuIndex, 1);

              let menuIndex = moreMenuList.findIndex(x => x.text === 'Send B/L to Partner');
              if (!appContext.suntekSettings?.edi?.ediblSuntekCustoms)
                moreMenuList.splice(menuIndex, 1);
            }
            break;
          case 'Air Import':
            if (currentUrl === storageKeyMap.get(JobNames.AIHbl) || history.location.search.includes(`jobname=${JobNames.AIHbl}`)) {
              const menuIndexArr = moreMenuList.findIndex(x => x.text === 'Arrival Notice' && x.link !== null);
              if (!systemSettings?.bl?.changeTitleInAnMenu)
                moreMenuList.splice(menuIndexArr, 1);
              else
                moreMenuList.splice(menuIndexArr + 1, 1);
            }
            break;
          case 'Billings':
            if (currentUrl === storageKeyMap.get(JobNames.GInvo) || history.location.search.includes(`jobname=${JobNames.GInvo}`)) {
              let menuIndex = moreMenuList.findIndex(x => x.text === 'Auto Billing');

              let suntekId = appContext.suntekSettings?.company?.companyId?.toUpperCase() ?? '';

              if (suntekId != 'FSSUNTEK')
                moreMenuList.splice(menuIndex, 1);
            }
            
            if (!appContext.suntekSettings?.general?.allowViewJournalBillings && !(appContext.user?.id??'').startsWith('@')) {
              moreMenuList = moreMenuList.filter(x => x.text !== constants.List_Journal);
            }

            break;
        }

        if (moreMenuList.length > 0) {
          let findOpendSideMenuIndex = menus?.job.findIndex(x => x.link?.includes(currentUrl)) ?? -1;

          // <250325 jhlee - Other Shipment의 경우만 특정 메뉴에 More Menu를 추가하도록 처리 />
          if (menus?.operation ===  constants.operations.otherShipment) {
            findOpendSideMenuIndex = menus?.job.findIndex(x => x.moreMenuJobName ===  JobNames.OtherOP) ?? -1;
          }
          
          if(moreMenuInjectBottom){
            menus?.job.splice(menus?.job.length + 1, 0, ...moreMenuList);
            menus.moreMenu = []; 
          }else if (findOpendSideMenuIndex !== -1) {
            menus?.job.splice(findOpendSideMenuIndex + 1, 0, { text: 'More Menu', level: 0, items: moreMenuList, metadata: { hideMenu: true } })
            menus.moreMenu = []; // clear more menus
          }
          else if (currentUrl.includes('jobname=')) {
            const jobName = GetMoreMenuJobName(currentUrl);
            const findParentMenuIndex = menus?.job.findIndex(x => x.moreMenuJobName?.includes(jobName)) ?? -1;
            if (findParentMenuIndex !== -1) {
              menus?.job.splice(findParentMenuIndex + 1, 0, { text: 'More Menu', level: 0, items: moreMenuList, metadata: { hideMenu: true } })
              menus.moreMenu = []; // clear more menus
            }
          } else if (opendOperationData?.jobName) {
            const findOpendDetailMenuIndex = menus?.job.findIndex(x => x.moreMenuJobName === opendOperationData?.jobName);
            menus?.job.splice(findOpendDetailMenuIndex + 1, 0, { text: 'More Menu', level: 0, items: moreMenuList, metadata: { hideMenu: true } })
            menus.moreMenu = []; // clear more menus
          }
        }
      }
      else
        menus.moreMenu.splice(0, menus.moreMenu.length);
    }

    // securityTaskCode filter
    if(menus.moreMenu && menus.moreMenu.length > 0) menus.moreMenu = MenuFilterGrents(menus.moreMenu, grents, appContext);
    if(menus.operationReport && menus.operationReport.length > 0) menus.operationReport = MenuFilterGrents(menus.operationReport, grents, appContext);
    if(menus.preReport && menus.preReport.length > 0) menus.preReport = MenuFilterGrents(menus.preReport, grents, appContext);
    if(menus.report && menus.report.length > 0) menus.report = MenuFilterGrents(menus.report, grents, appContext);
    if(menus.afterReport && menus.afterReport.length > 0) menus.afterReport = MenuFilterGrents(menus.afterReport, grents, appContext);
      
    // if(operationState.sideMenu==='Utility'){
    //   if(activeUrlRef.current.includes('utMemotask')){
    //     menus.job = menus.job.filter(x => x.text === 'Attached File List')
    //   }
    //   else if(activeUrlRef.current.includes('utAttachedfile')){
    //     menus.job = menus.job.filter(x => x.text === 'Memo/Task List')
    //   }
    //   else if(activeUrlRef.current.includes('utEmail')){
    //     menus.job = menus.job.filter(x => x.text !== 'Email Log List' && x.text?.toString().includes('Log'))
    //   }
    //   else if(activeUrlRef.current.includes('utPrint')){
    //     menus.job = menus.job.filter(x => x.text !== 'Print Log List' && x.text?.toString().includes('Log'))
    //   }
    //   else if(activeUrlRef.current.includes('utFax')){
    //     menus.job = menus.job.filter(x => x.text !== 'Fax Log List' && x.text?.toString().includes('Log'))
    //   }
    // }

    if (menus.operation === constants.operations.utility) {
      menus.job =
        menus.job.map(job => {
          const tempData = String(job.text).includes('B/L Activity Status');
          if (tempData) {
            if (String(job.text).includes('OIH')) {
              job.text = appContext.systemSettings?.showMilestoneInOihList ? 'OIH B/L Activity Status Off' : 'OIH B/L Activity Status On'
            } else {
              job.text = appContext.systemSettings?.showMilestoneInAihList ? 'AIH B/L Activity Status Off' : 'AIH B/L Activity Status On'
            }
          }
          return { ...job };
        });
    }

    // 250203 jhchoi : #1895 - Payroll (Old) 데이터가 있는 경우에만 Payroll(Old) Menu를 표시(notShowOldPayroll 값은 Fix Query를 통해 설정된다.)
    if (menus.operation === constants.operations.accounting) {
      const notShowOldPayroll = appContext.suntekSettings?.general?.notShowOldPayroll?? false;
      if (notShowOldPayroll) menus.job = menus.job.filter(x => x.text !== 'Payroll (Old)');   
    }
  }

  function unsubscribeAll() {
    unsubscribeChannels();

    //<pt220314>
    if(broadcastChannel) {
      window.removeEventListener('unload', (e) => {
        if (window.name.indexOf('ifmssub') === -1) broadcastChannel?.postMessage({Target:'CloseWindow'});
      });
      broadcastChannel.removeEventListener('message', bcMessageHandler);
      broadcastChannel.close();
    }

    window.removeEventListener('mousemove', tooltipMonitor);
    window.removeEventListener('keydown', (e) => {
      globalDirtyMarkCleaner(e);
      appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current//, autoLogoutReaminTimerId.current
      ]);
    });
    window.removeEventListener('click', (e) => {
      appContext.logoutWorker?.postMessage(['setAutoLogoutTimer', autoLogoutTimerId.current//, autoLogoutReaminTimerId.current
      ]);
    });

    appContext.logoutWorker?.terminate();
  }

  function subscribeChannels() {
    navigationSubscription = postal.subscribe({
      channel: 'ifms-navigation',
      topic: 'ifms-navigation.urlChange',
      callback: link => {
        if (activeUrlRef.current !== link) {
          activeUrlRef.current = link;
          const ish = defaultUrls.includes(link);
          if (isHomeRef.current !== ish) {
            isHomeRef.current = ish;
            setIsHome(ish);
          }
        }
      }
    });
    ajaxErrorSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.error',
      callback: error => onApiError(error)
    });
    ajaxStartSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.start',
      callback: () => setApiLoading(true)
    });
    ajaxEndSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.end',
      callback: () => setApiLoading(false)
    });
    loadingStartSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.loadingstart',
      callback: () => setApiLoadingMessage(true)
    });
    loadingEndSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.loadingend',
      callback: () => setApiLoadingMessage(false)
    });
    viewReportSubscription = postal.subscribe({
      channel: 'report',
      topic: 'report.view',
      callback: ({reportId, opType, jwt}) => {
        //<pt220406: optab-rptviewer
        // if (reportRef && reportRef.current) {
        //   reportRef.current.viewReport(reportId);
        // }
        //debugger;
        openNewPage(history, `/common/report/${encodeURIComponent(reportId)}/${opType}`, NavigationType.newPage);
        //pt220406>
      }
    });
    // move to login page //<240221.dwshin>
    movetologinSubscription = postal.subscribe({
      channel: 'ajax',
      topic: 'ajax.movetologin',
      callback: () => moveToLoginPage()
    });
  }

  function unsubscribeChannels() {
    if (navigationSubscription) {
      postal.unsubscribe(navigationSubscription);
    }
    if (ajaxErrorSubscription) {
      postal.unsubscribe(ajaxErrorSubscription);
    }
    if (ajaxStartSubscription) {
      postal.unsubscribe(ajaxStartSubscription);
    }
    if (ajaxEndSubscription) {
      postal.unsubscribe(ajaxEndSubscription);
    }
    if (loadingStartSubscription) {
      postal.unsubscribe(loadingStartSubscription);
    }
    if (loadingEndSubscription) {
      postal.unsubscribe(loadingEndSubscription);
    }
    if (viewReportSubscription) {
      postal.unsubscribe(viewReportSubscription);
    }
    if (tableEditSubscription) {
      postal.unsubscribe(tableEditSubscription);
    }
    if (movetologinSubscription) {
      postal.unsubscribe(movetologinSubscription);
    }
  }

  function redirectToUserDefaultUrl() {
    if (!userLogin.user) {
      return;
    }

    // const user = userLogin.user;

    // if (user.defaultOperation === constants.operations.oceanExport) {
    //   defaultUrl = '/oceanExport/home';
    // }
    // if (user.defaultOperation === constants.operations.airImport) {
    //   defaultUrl = '/airExport/home';
    // }
    // if (user.defaultOperation === constants.operations.airExport) {
    //   defaultUrl = '/airExport/home';
    // }
    // if (user.defaultOperation === constants.operations.otherShipment) {
    //   defaultUrl = '/otherShipment/home';
    // }
    // if (user.defaultOperation === constants.operations.warehousing) {
    //   defaultUrl = '/warehousing/home';
    // }
    // if (user.defaultOperation === constants.operations.accounting) {
    //   defaultUrl = '/accounting/home';
    // }


    if (userLogin.user.moveToAutoLogin) {
      moveToAotherPage(userLogin.user.moveToAutoLogin);
    }else if (currentUrl !== '/home/home') {
      openNewPage(history, '/home/home', NavigationType.newPage);
    }
  }

  function moveToAotherPage(info: string){

    const moveInfo = info.split(';');
    // type=detail;url=url                // array 2
    // type=list;url=url;searchinfo=info  // array 3
    // type=list;url=url;searchinfo=info;title=title  // array 4
    
    
    // type detail
    if (moveInfo?.length > 1) {
      var type = moveInfo[0].replace('type=', '');

      // title change
      //window.document.title = 'test';
     if(moveInfo?.length> 3) window.document.title = moveInfo[3].replace('title=', '');

      switch (type) {
        case 'detail':
          var url = moveInfo[1].replace('url=', '');
          window.name = `ifmssub-hblinfo-${url}-${appContext.user?.suntekId?.toLowerCase()}-${appContext.user?.id?.toLowerCase()}`;
          openNewPage(history, url, NavigationType.newPage);

          break;
        // case 'list':
        //     var url = moveInfo[1].replace('url=', '');
        //     //console.log(`url`, url);
        //     eval(url);
        //   break;
      }

    }

  }

  function globalDirtyMarkCleaner(e:KeyboardEvent) {
    const element = e.target;
    if (element && element instanceof HTMLElement ) {
      const validationFor = element?.dataset?.validationfor;
      if (validationFor) {
        const dirtyElementsForProperty = document.querySelectorAll(`[data-validationfor="${validationFor}"]`);
        for (let i = 0; i < dirtyElementsForProperty.length; ++i) {
          dirtyElementsForProperty[i].classList.remove('ifms-validation-error');
        }
      }

      element.classList.remove('ifms-validation-error');
    }
  }

  function isHTMLElement(element: Element): element is HTMLElement {
    return 'className' in element && (typeof element.className === 'string');
  }

  function tooltipMonitor(e:MouseEvent) {
    const hoverElements = document.elementsFromPoint(e.clientX, e.clientY);

    const hasHeaderFilterModal = hoverElements.some(element => isHTMLElement(element) && element.className.includes("header-filter-modal"));

    if(hasHeaderFilterModal) {
      deleteAllTooltipsFromDom();
      return;
    }

    if (!hoverElements || hoverElements.length <= 0) {
      if (tooltipElement) {
        tooltipElement = null;
        processTooltip();
      }
      return;
    }

    for (let i = 0; i < hoverElements.length; ++i) {
      const el = hoverElements[i];
      if (!(el instanceof HTMLElement || el instanceof SVGElement)) {
        continue;
      }
      if (el.dataset.tooltip) {
        if (el === tooltipElement) {
          return;
        }

        tooltipElement = el;
        processTooltip();
        return;
      }
    }

    if (tooltipElement) {
      tooltipElement = null;
      processTooltip();
    }
  }

  function processTooltip() {
    const isBlank = (str:string|null|undefined) => (!str || /^\s*$/.test(str));

    if (tooltipElement) {
      if (!!tooltipElement.dataset.tooltipOnlywhencutoff) {
        if (tooltipElement.scrollWidth <= tooltipElement.clientWidth) {
          deleteAllTooltipsFromDom();
          return;
        }
      }
      let tooltipText = tooltipElement.dataset.tooltip;
      if (tooltipText === '-from-value' && !(tooltipElement instanceof SVGElement)) {
        const inputElement = tooltipElement as HTMLInputElement;
        if (inputElement.value) {
          if (inputElement.value.length > 200)
            tooltipText = inputElement.value.slice(0, 100) + '\r\n' + inputElement.value.slice(100, 200) + '\r\n' + inputElement.value.slice(200);
          else if (inputElement.value.length > 100)
            tooltipText = inputElement.value.slice(0, 100) + '\r\n' + inputElement.value.slice(100);
          else 
            tooltipText = inputElement.value;
        } else {
          if (tooltipElement.innerText.length > 200)
            tooltipText = tooltipElement.innerText.slice(0, 100) + '\r\n' + tooltipElement.innerText.slice(100, 200) + '\r\n' + tooltipElement.innerText.slice(200);
          else if (tooltipElement.innerText.length > 100)
            tooltipText = tooltipElement.innerText.slice(0, 100) + '\r\n' + tooltipElement.innerText.slice(100);
          else
            tooltipText = tooltipElement.innerText;
        }
      }

      if (isBlank(tooltipText)) {
        deleteAllTooltipsFromDom();
        return;
      }

      let fragment : DocumentFragment = createFragment('');
      
      // const userButtonMenu = document.getElementsByClassName('ifms-custom-usermenu-plain');
      // if (userButtonMenu.length === 0)
      fragment = createFragment(`<div class="ifms-tooltip" style="position: absolute; display: inline-block;">${tooltipText}</div>`);

      const firstChildNode = fragment.childNodes[0];
      if (!(firstChildNode instanceof HTMLElement)) {
        return;
      }
      
      const fragmentPosition = getTooltipPosition(firstChildNode);

      firstChildNode.style.left = `${fragmentPosition.left}px`;
      firstChildNode.style.top = `${fragmentPosition.top}px`;
      
      deleteAllTooltipsFromDom();
      document.body.appendChild(firstChildNode);

      // Tooltip 위치 보정 - Right 넘어갈 때
      if (firstChildNode.offsetWidth + fragmentPosition.left > document.body.offsetWidth) {
        firstChildNode.style.left = `${document.body.offsetWidth - firstChildNode.offsetWidth}px`
        //firstChildNode.style.left = `${document.body.offsetWidth - firstChildNode.offsetWidth - 5}px`
      }
      // Is special tooltip (INV, BIL, Consignee, Shipper...)
      if (tooltipElement.dataset.tooltipPosition === 'listview-cell') {
        const childInfo = firstChildNode.getBoundingClientRect()
        firstChildNode.style.top = `${childInfo.top - childInfo.height}px`;
      }
    } else {
      deleteAllTooltipsFromDom();
    }
  }

  function deleteAllTooltipsFromDom() {
    const visibleTooltips = document.querySelectorAll('.ifms-tooltip');
      if (visibleTooltips && visibleTooltips.length > 0) {
        for (let i = 0; i < visibleTooltips.length; ++i) {
          const t = visibleTooltips[i];
          if (t.parentElement) {
            t.parentElement.removeChild(t);
          }
        }
      }
  }

  async function onApiError(error:any) {
    const color = error.errorType === 'warning' ? '#f7c82d' : '#bd3737';
    await showGlobalOkDialog(
      <div>
        <div>
          <h5>
            <span style={{color}}><FontAwesomeIcon icon={faExclamationTriangle} /></span> {error.errorTitle}
          </h5>
        </div>
        <div dangerouslySetInnerHTML={{__html: error.errorText}}></div>
      </div>
    );
  }

  async function purgeLocalStorage() {
    const keys = await localforage.keys();

    const toDelete:Array<string> = [];
    keys.forEach(k => {
      if (k.startsWith('filter-')) {
        toDelete.push(k);
      }
    });

    for (let i = 0; i < toDelete.length; ++i) {
      await localforage.removeItem(toDelete[i]);
    }

    _.forOwn(fastStorage, (_, k) => {
      delete fastStorage[k];
    });
  }

  async function logoutUser(showLogoutAlert: boolean = true, authLogoff: boolean = true) {
    // Find are there any screens that should be closed on logout and prompt for confirmation
    function distinct(value:JobMenuItem, index:number, self:Array<JobMenuItem>) {
      return self.findIndex(x => stringsEqualIgnoreCase(x.url, value.url)) === index;
    }

    // const jobMenuItemsToCloseOnLogout = [
    //   ...jobMenu.oceanImport.filter(x => x.closeOnLogout),
    //   ...jobMenu.oceanExport.filter(x => x.closeOnLogout),
    //   ...jobMenu.airImport.filter(x => x.closeOnLogout),
    //   ...jobMenu.airExport.filter(x => x.closeOnLogout),
    //   ...jobMenu.otherShipment.filter(x => x.closeOnLogout),
    //   ...jobMenu.accounting.filter(x => x.closeOnLogout),
    //   ...jobMenu.warehousing.filter(x => x.closeOnLogout),
    // ].filter(distinct);

    // Implementation note:
    //   Remove all screens on logout, not just children, but leave the code in case it needs to be turned back on
    const allJobMenuItemsToCloseOnLogout = [
      ...jobMenu.home,
      ...jobMenu.oceanImport,
      ...jobMenu.oceanExport,
      ...jobMenu.airImport,
      ...jobMenu.airExport,
      ...jobMenu.otherShipment,
      ...jobMenu.whReceipt,
      ...jobMenu.accounting,
      ...jobMenu.warehousing,
      ...jobMenu.opReport,
      ...jobMenu.customer,
      ...jobMenu.standalone,
      ...jobMenu.utility,
      ...jobMenu.billings,
      // ...jobMenu.brokerageTrucking,
      ...jobMenu.order,
      ...jobMenu.load,
      ...jobMenu.localDO,
      ...jobMenu.ams,
      ...jobMenu.isf
    ].filter(x => !stringsEqualIgnoreCase('Home', x.label)).filter(distinct);

    let doLogout = true;

    if (showLogoutAlert && allJobMenuItemsToCloseOnLogout.length > 0) {
      doLogout = await showGlobalDialog(
        <div>
          <h3>Are you sure you want to logout?</h3>

          {/* There are opened screens:
          <br />
          <br />
          {allJobMenuItemsToCloseOnLogout.map((x, i) => (
            <li key={i}>{x.label}</li>
          ))} */}
        </div>
      );
    }

    if (doLogout) {
      if(authLogoff && (process.env.NODE_ENV === 'production')){
        // authentication service logoff call
        await appContext.api.authenticateLogoff();
      }
      await purgeLocalStorage();

      // report tab all close
      if (window.name.indexOf('ifmssub') === -1) broadcastChannel?.postMessage({Target:'CloseWindow'});
      // clear logout timeout.
      appContext.logoutWorker?.postMessage(['clearTimeout', autoLogoutTimerId.current]);

      cleanupAll();

      dispatch(clearStorage());
      dispatch(clearOperation());
      dispatch(resetListScreenSettings());
      dispatch(logout());
      dispatch(clearLastSavedJobs());

      appContext.onLogout();

      history.push('/');
    }
  }

  async function moveToLoginPage(){
    let userId = appContext?.user?.id;

    await showGlobalOkDialog(
      <div>
        <h3>Someone has logged in with the same ID{userId ? `(${userId})` : '' }
          <br />So You are automatically logged out.</h3>
      </div>
    );
    
    await logoutUser(false, false);
  }

  // Memo screen does not belong to any operation but must ensure OP menu from where
  // it was opened is displayed
  function prepareMemoListView(menuItem:IfmsConfigMenuItem) {
    if (!menuItem.link || !menuItem.metadata || !menuItem.metadata.op) {
      // Should never happen
      return;
    }

    const parts = menuItem.link.split('/');

    const requestingOperation = getOperationKey(menuItem.metadata.op);
    const viewIndex = parts[2];

    const hasViewIndex = memoStorage.hasViewIndex(viewIndex);

    if (hasViewIndex) {
      const openedOperation = getOperationKey(memoStorage.getViewIndex(viewIndex));

      if (!requestingOperation || !openedOperation) {
        // Should never happen
        return;
      }

      if (openedOperation === requestingOperation) {
        return;
      }

      // Replace screen under different operation before opening

      dispatch(moveJobToOperation({
        destinationOperation: requestingOperation,
        sourceOperation: openedOperation,
        url: menuItem.link,
      }));
      dispatch(moveScreenToOperation({
        destinationOperation: requestingOperation,
        sourceOperation: openedOperation,
        url: menuItem.link,
      }));
      dispatch(changeOperation(menuItem.metadata.op));
      dispatch(changeSideMenu(menuItem.metadata.op));

      memoStorage.deleteViewIndex(viewIndex);
    }
  }

  //for HelpButton
  async function openESupportPage() {
    const openURL: string = await appContext.api.getESupportURL(fmsGlobal.AcmeID)
    window.open(openURL);
  }

  //for HelpButton
  async function checkFileServer() {

      setApiLoadingWithMessage("Please wait while checking file server...");

    const apiResult2: ApiResult<InsertResultsWithData> = await appContext.api.checkfileserver();
    if (apiResult2 && apiResult2.success) {
        setApiLoadingWithMessage(null);
      await showGlobalOkDialog(<p>File Server is working!</p>);
    }
    else {
        setApiLoadingWithMessage(null);
      await showGlobalOkDialog(<p>File Server is not working!</p>);
    }
  }

  //for HelpButton
  async function setMilestoneOption(jobName: string) {
    let msg = '';
    if (jobName === 'OIH') {
      if (hasOpenedScreenStartingWithUrl('/common/joblistview/hbl/oceanImport')) {
        await showGlobalOkDialog(<p>Please close HBL List (OI)</p>);
        return;
      }
      let sqlInfos: SQLInfo[] = [];
      sqlInfos.push(new SQLInfo({
        tableName: "T_SETTINGS",
        type: SQLType.Update,
        fields:[
          new SQLField({fieldName: "F_Value", propertyName:'showMilestoneInOihList',value: !appContext.systemSettings?.showMilestoneInOihList})
        ],
        whereFields:[
          new SQLField({fieldName:"F_Name", propertyName:'', value:'ShowMilestoneInOihList'})
        ]
      }))

      const apiResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.SystemSetup,
        sqlInfos,
        {});

       if(apiResult && apiResult.success){
        appContext.setSystemSettings({
          ...appContext.systemSettings,
          showMilestoneInOihList: !appContext.systemSettings?.showMilestoneInOihList,
        });

        msg = appContext.systemSettings?.showMilestoneInOihList ? 'OIH B/L Activity Status Off' : 'OIH B/L Activity Status On'
        await showGlobalOkDialog(<p>{msg}</p>);
       }  
    }
    else {
      if (hasOpenedScreenStartingWithUrl('/common/joblistview/aiHawb/airImport')) {
        await showGlobalOkDialog(<p>Please close HAWB List (AI)</p>);
        return;
      }
      
      let sqlInfos: SQLInfo[] = [];
      sqlInfos.push(new SQLInfo({
        tableName: "T_SETTINGS",
        type: SQLType.Update,
        fields:[
          new SQLField({fieldName: "F_Value", propertyName:'showMilestoneInAihList', value: !appContext.systemSettings?.showMilestoneInAihList})
        ],
        whereFields:[
          new SQLField({fieldName:"F_Name", propertyName:'', value:'ShowMilestoneInAihList'})
        ]
      }))
      const apiResult: ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(JobNames.SystemSetup,
        sqlInfos,
        {});

      if(apiResult && apiResult.success){
        appContext.setSystemSettings({
          ...appContext.systemSettings,
          showMilestoneInAihList: !appContext.systemSettings?.showMilestoneInAihList,
        });
        msg = appContext.systemSettings?.showMilestoneInAihList ? 'AIH B/L Activity Status Off' : 'AIH B/L Activity Status On'
        await showGlobalOkDialog(<p>{msg}</p>);
      }
    }
  }

  async function checkStandaloneDirty() {
    if(selectedOperation !== 'Standalone') return;

    if (fastStorage['ifms-settings-haschanges']) {
      const doSave = await showGlobalYesNoDialog(
        <div>
          There are unsaved changes. Do you want to save?
        </div>
      ,false, 1, true, 'info');

      const externalFunctions = getExternalFunctions();
      if (doSave) {
        await externalFunctions.ifmsSettingsSave();
      } else {
        await externalFunctions.ifmsSettingsCancel();
      }
    }
    else if (fastStorage['ifms-referenceFiles-haschanges']) {
      const doSave = await showGlobalYesNoDialog(
        <div>
          There are unsaved changes. Do you want to save?
        </div>
      ,false, 1, true, 'info');

      const externalFunctions = getExternalFunctions();
      if (doSave) {
        await externalFunctions.ifmsReferenceFilesSave();
      } else {
        await externalFunctions.ifmsReferenceFilesCancel();
      }
    }
  }

  async function onMenuItemClicked(menuItem:IfmsConfigMenuItem) {
    if (menuItem.disable) return;

    if (menuItem.link && !menuItem.link.includes('http')) await checkStandaloneDirty();
    // if (menuItem.changeOperation) {
    //   if (menuItem.changeOperation === selectedOperation) {
    //     return;
    //   } else {
    //     dispatch(changeOperation(menuItem.changeOperation));
    //   }
    // }
    if (menuItem.changeOperation) {
      if (menuItem.changeOperation !== selectedOperation) {
        dispatch(changeOperation(menuItem.changeOperation));
      }
    }
    if (menuItem.changeSideMenu) {
      if (menuItem.changeSideMenu !== operationState.sideMenu) {
        dispatch(changeSideMenu(menuItem.changeSideMenu));
      }
    }

    if (menuItem.name === 'amsFiling') {
      const apiResult = await appContext.api.getAmsLink();
      const isAmsEnable = appContext.suntekSettings?.compliance?.amsEnable;

      if (apiResult && apiResult.success) {
        if (isAmsEnable) {
          window.open('https://ams1.suntekscm.com/login_auto.aspx?' + apiResult.result);
        } else {
          window.open('https://ams1.suntekscm.com/login_2017.aspx');  
        }
      } 
    }

    if (menuItem.name === 'isfFiling') {
        const apiResult = await appContext.api.getIsfLink();
        if (apiResult && apiResult.success) {
            window.open('https://imanifest.suntekscm.com/Home/AutoLoginFms?args=' + apiResult.result);
        }
    }

    if (menuItem.name === 'isfUpload') {
      const apiResult = await appContext.api.getIsfLink('upload');
      if (apiResult && apiResult.success) {
        window.open('https://imanifest.suntekscm.com/Home/AutoLoginFms?args=' + apiResult.result);
      }
    }

    if (menuItem.name === 'isfExcelDownload') {
      const link = document.createElement('a');
      link.href = '/template/Suntek ISF Filing Template.xlsx'; // Relative path to your public file
      link.download = 'Suntek ISF Filing Template.xlsx'; // Suggested filename for download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    if (!!menuItem.link) {
      // Special case scenario
      if (menuItem.link.startsWith('/memoList')) {
        prepareMemoListView(menuItem);
      }

      // if (menuItem.link.indexOf('/releaseOrderTurnover') > 0) {
      //   if (fmsGlobal.NoRelease) {
      //     showGlobalOkDialog('Contact Manager!');
      //     return false;
      //   }
      // }

      if (menuItem.link === history.location.pathname) {
        // The menu item clicked is the same screen we are on. In case it is a list screen, clear filters on that page
        filterChannel.publish('ifms-filter.forceclear');

        // Metadata could be changed. Make sure it is replaced. Both on screen stack and in job menu
        dispatch(replaceJobMenuMetadata({ url: menuItem.link, metadata: menuItem.metadata}));
        dispatch(replaceScreenStackMetadata({url: menuItem.link, metadata: menuItem.metadata}));
      }

      if (menuItem.link.indexOf('parentOp') > -1) {
        if (menuItem.link === history.location.pathname + history.location.search) {
          filterChannel.publish('ifms-filter.forceclear');
        }
        dispatch(replaceJobMenuParentOp({ url: menuItem.link }));
        dispatch(replaceScreenUrlParentOpOnStack({ url: menuItem.link, metadata: menuItem.metadata }));
      }

      if (menuItem.moreMenuJobName && menuItem.level !== 0) {
      // if (menuItem.moreMenuJobName && menuItem.level === 0) {
        //listSelectedId is Empty
        let lastSelectedId = document.getElementById('last-selectedId')?.innerText ?? '';
        let jobName = currentListJobName === JobNames.None ? getJobNamesByCurrentUrl(storageKeyMap, activeUrlRef.current) : currentListJobName;

        //Click moremenu in moremenu tab
        if (_.isEmpty(lastSelectedId)) {
          const params = history.location.search.replace('?', '').split('&');
          lastSelectedId = params.find(x => x.includes('id='))?.replace('id=', '') ?? '';
          jobName = params.find(x => x.includes('jobname='))?.replace('jobname=', '') as JobNames;

          //Click moremenu in operation detail tab
          if (_.isEmpty(lastSelectedId)) {
            jobName = opendOperationData?.jobName ?? JobNames.None
            //lastSelectedId = document.getElementById(`${opendOperationData?.jobName}-detailid`)?.innerText ?? ''
            lastSelectedId = opendOperationData?.jobParams.id ?? ''
          } 
        }

        let link = menuItem.link.replace('@jobName', jobName).replace('@jobid', lastSelectedId);
        if (_.isEmpty(jobName) || _.isEmpty(lastSelectedId)) return;
        if (jobName === JobNames.OIHbl && menuItem.text === 'CBP Status Query') {
          const oiMblId = document.getElementById('last-selectedOiMblId')?.innerText ?? '';
          link = link.replace('@oiMblId', oiMblId);
        }

        if (lastSelectedId === '0' && menuItem.moreMenuJobName === jobName) {
          await showGlobalOkDialog(<p>Data is not select!</p>);
        } else {
          openNewPage(history, link, NavigationType.newPage, menuItem.metadata);
        }

        return;
      }

      if (menuItem.link?.includes('http'))
        window.open(menuItem.link);
      else
        openNewPage(history, menuItem.link, NavigationType.newPage, menuItem.metadata);
    }

    if (!!menuItem.popupComponent) {
      if (menuItem.name === 'cfsRelease') {
        const jobId = document.getElementById('last-selectedId')?.innerText ?? '';
        const rlsStat = document.getElementById('last-selectedRlsStat')?.innerText ?? '';

        const apiResult = await appContext.api.checkReleaseOrder(jobId);
        if (apiResult && apiResult.success) {
          const opt = apiResult.result as CFSManifestReleaseOption;
          if (opt === null || !opt.requestDept || !opt.email)
            await showGlobalOkDialog(<p>CFS/Contact not configured for email release.<br></br>
              (Name='ediRelease', Dept=NVOID, EMail=Address)</p>);
          else {
            // var rlsYN = jobListView.ListRenderer.OihCellClickHandler("RlsStat", oihListItem); 
            if (rlsStat === 'Y') {
              const doSave = await showGlobalYesNoDialog(
                <div>
                  Do you want to release?
                </div>
                , false, 0, true, 'info');

              if (doSave) { //EDI 메일폼열기
                const vContents = await appContext.api.makeCfsRelease({ id: +jobId, contact: opt.contact, requestDept: opt.requestDept })
                viewEmptyReport(false, opt.companyId.toString(), 'Release', vContents?.message ?? '' , 'T_OIHMAIN', +jobId, true);
              }
            }
            else {
              menuItem.popupComponent =
                <HblReleasePopup hblId={+jobId}
                  storageKey={''} onClose={function (x: ReleaseHblInfo | null): void {
                    const storageKey = `oih-hblrls-${jobId}`;
                    fastStorage[storageKey] = null;
                    setSelectedPopupMenuItem(null);
                  }} isView={false} fromCFS={true}
                />;
              setSelectedPopupMenuItem(menuItem)
            }
          }
        }
      }
      //Period Close가 사용되고 있음
      else setSelectedPopupMenuItem(menuItem)
    }

    if (!!menuItem.subMenu || !!menuItem.subMenuNonReport) {
      setSelectedReportMenuItem(menuItem)
    } 

    //Move to ifmsHelp.tsx
    //for HelpButton 
    if (operationState.sideMenu === constants.operations.utility) {
      if (menuItem.text?.toString() === 'Check File Server')
        checkFileServer();
      else if (menuItem.text?.toString().includes('B/L Activity Status')) {
        menuItem.text.toString().includes('OIH') ? setMilestoneOption('OIH') : setMilestoneOption('AIH');
      }
      else if (menuItem.text?.toString() === 'eSupport') {
        openESupportPage();
      }
    }

    if (operationState.sideMenu === constants.operations.accounting) {
      if (menuItem.text?.toString() === 'iXpense')
        return;
        //await showGlobalOkDialog(<p>Please Contact Suntek to Activate</p>);
    }
  }

 async function getSelectedElement(model: any) {
    if (model) {
      if (listData && listData.length > 0) {
        return listData?.find((x: any) => String(x.id) === listSelectedId) as typeof model;
      }
    }
    else {
      //Click moremenu in moremenu tab
      const params = history.location.search.replace('?', '').split('&');
      let jobName = params.find(x => x.includes('jobname='))?.replace('jobname=', '') as JobNames;
      let jobId = params.find(x => x.includes('id='))?.replace('id=', '') ?? '';

      let job = null;
      let listView = null;
      

      model = getListItemByJobName(jobName);
      
      if (!model) {//Click moremenu in operation detail tab
        jobName = opendOperationData?.jobName ?? JobNames.None;
        jobId = opendOperationData?.jobParams?.id ?? 0;

        model = getListItemByJobName(jobName);
        //Operation Menu의 List가 켜져있지 않은 상태에서 Detail View에 접근 후 Report Menu Click시 Report가 표시되지 않는 문제 수정
        job = getJobFromName(jobName);
        listView = job.generateListView();
      }
      const listData = fastStorage[`ifms-list-data-${storageKeyMap.get(jobName)}`];
      if(listData){
        return listData?.find((x: any) => String(x.id) === jobId) as typeof model ?? model;
      } else {
        //Operation Menu의 List가 켜져있지 않은 상태에서 Detail View에 접근 후 Report Menu Click시 Report가 표시되지 않는 문제 수정
        if(listView){
          const data = await listView.fetchAndFormatData(appContext.api, { ...initialSearch, ...{ general1: 'id', generalText1: jobId ?? '' } }, undefined, appContext.user, jobListContext);
          console.log(data);
          return data.data?.find((x: any) => String(x.id) === jobId) as typeof model ?? model;
        }
      }
      return model;
    }
  

 
  }

  async function onSubMenuClick (fmsSubMenu: FmsSubMenu, model: any, jobName: JobNames) {
    setSelectedReportMenuItem(null);
    if (!jobName) {
      jobName = opendOperationData?.jobName ?? JobNames.None;
    }

    const selectedElement = await getSelectedElement(model);
    if (!selectedElement) {
      return;
    }


    let fullLink = '';
    let searchText = '';
    if (fmsSubMenu == FmsSubMenu.NewCustomerPay || fmsSubMenu == FmsSubMenu.NewBillPay) {
    //if(opendOperationData) return;

      let link = `/accounting/${fmsSubMenu == FmsSubMenu.NewCustomerPay ? 'acCustomerPayment' : 'acBillPayment'}/add`;
      switch (jobName) {
        case JobNames.OpInvoice:
        case JobNames.Invoice:
        case JobNames.GInvo:
          fullLink = `${link}/${selectedElement.invoiceTo}`;
          break;
        case JobNames.OpBill:
        case JobNames.Bill:
        case JobNames.GBill:
          fullLink = `${link}/${selectedElement.vendorId}`;
          break;
        case JobNames.OpCrDb:
        case JobNames.CrDb:
          fullLink = `${link}/${selectedElement.agentId}`;
          break;
      }
      // New Mode를 동시에 열 수 없도록 수정
      if (hasOpenedScreenStartingWithUrl(link)) {
        await showGlobalOkDialog(<p>New operation is already in progress</p>);
        return;
      }
      moveToSingleOpenedScreenInAnyOperationStartingWithUrl(fullLink, [fullLink]);
      return;
    }

    if (fmsSubMenu == FmsSubMenu.CustomerPayList || fmsSubMenu == FmsSubMenu.BillPayList) {
      //if(opendOperationData) return;
      fullLink = `/common/joblistview/${fmsSubMenu == FmsSubMenu.CustomerPayList ? 'acCustomerPayment' : 'acBillPayment'}/accounting`;

      switch (jobName) {
        case JobNames.OpInvoice:
        case JobNames.Invoice:
        case JobNames.GInvo:
          searchText = selectedElement.invoiceNo;
          break;
        case JobNames.OpBill:
        case JobNames.Bill:
        case JobNames.GBill:
          searchText = selectedElement.billNo;
          break;
        case JobNames.OpCrDb:
        case JobNames.CrDb:
          searchText = selectedElement.crDbNo;
          break;
      }

      //setFilterForScreenExternally(fullLink, 'invoiceNo', 'Billings', searchText, 'updated', false, true, null);
      openNewPage(history, fullLink, NavigationType.newPage);
      return;
    }

    if (fmsSubMenu === FmsSubMenu.SwitchJournal) {
    //if(opendOperationData) return;
      switch (jobName) {
        case JobNames.OpInvoice:
        case JobNames.Invoice:
          switchJournal(JobNames.Invoice, selectedElement.id);
          break;
        case JobNames.GInvo:
          switchJournal(JobNames.GInvo, selectedElement.id);
          break;
        case JobNames.OpBill:
        case JobNames.Bill:
          switchJournal(JobNames.Bill, selectedElement.id);
          break;
        case JobNames.GBill:
          switchJournal(JobNames.GBill, selectedElement.id);
          break;
        case JobNames.OpCrDb:
        case JobNames.CrDb:
          switchJournal(JobNames.CrDb, selectedElement.id)

          break;
      } 
      return;
    } 

    // if (fmsSubMenu === FmsSubMenu.OpenHistoryView || fmsSubMenu === FmsSubMenu.OpenDeleteHistoryView) {
    //   const viewAcctDeleteHistory = fmsSubMenu === FmsSubMenu.OpenDeleteHistoryView;
    //   let width = 1200;

    //   if (viewAcctDeleteHistory || [JobNames.OpBill, JobNames.Bill, JobNames.GBill].includes(jobName))
    //     width = 920;

    //   if (viewAcctDeleteHistory) width = 640;

    //   let deleteTitle = "";
    //   switch (jobName) {
    //     case JobNames.Invoice:  deleteTitle = "Invoice"; break;
    //     case JobNames.Bill:  deleteTitle = "Bill"; break;
    //     case JobNames.CrDb:  deleteTitle = "CrDb"; break;
    //     case JobNames.GInvo:  deleteTitle = "G. Invoice"; break;
    //     case JobNames.GBill:  deleteTitle = "G. Bill"; break;
    //   }

    //   setSelectedReportPopupMenuItem({ 
    //     label: fmsSubMenu === FmsSubMenu.OpenHistoryView ? 'View History' : 'Delete History (' + deleteTitle + ')',
    //     popupWidth: width,
    //     popupTop: fmsSubMenu === FmsSubMenu.OpenHistoryView ? 0 : 50,
    //     popupComponent: (<HistoryView jobId={selectedElement.id} jobType={selectedElement.jobType} jobName={jobName} onPopupClose={() => setSelectedReportPopupMenuItem(null)} viewDeleteHistory={viewAcctDeleteHistory} />)
    //   });
    //   return;
    // }

    if (fmsSubMenu === FmsSubMenu.OpenHistoryView) {
      let width = 1200;

      if ([JobNames.OpBill, JobNames.Bill, JobNames.GBill].includes(jobName))
        width = 920;

      let deleteTitle = "";
      switch (jobName) {
        case JobNames.Invoice:  deleteTitle = "Invoice"; break;
        case JobNames.Bill:  deleteTitle = "Bill"; break;
        case JobNames.CrDb:  deleteTitle = "CrDb"; break;
        case JobNames.GInvo:  deleteTitle = "G. Invoice"; break;
        case JobNames.GBill:  deleteTitle = "G. Bill"; break;
      }

      setSelectedReportPopupMenuItem({ 
        label: `View History (${deleteTitle})`,
        popupWidth: width,
        popupTop: fmsSubMenu === FmsSubMenu.OpenHistoryView ? 0 : 50,
        popupComponent: (<HistoryView jobId={selectedElement.id} jobType={selectedElement.jobType} jobName={jobName} onPopupClose={() => setSelectedReportPopupMenuItem(null)} />)
      });
      return;
    }

    if (fmsSubMenu === FmsSubMenu.PrintBill) {
      setSelectedReportPopupMenuItem({
        label: 'Print Bill',
        popupWidth: 540,
        popupComponent: (<PrintBill selectedElement={selectedElement} onPopupClose={() => setSelectedReportPopupMenuItem(null)} />)
      });
      return;
    }

    if (fmsSubMenu === FmsSubMenu.BatchPrint) {
      setSelectedReportPopupMenuItem({
        label: 'Batch Print',
        popupWidth: 760,
        popupComponent: (<BatchPrint selectedElement={selectedElement} documentType={DocumentType.InvoiceS} jobName={JobNames.RptInvoiceS} onPopupClose={() => setSelectedReportPopupMenuItem(null)} />)
      });
      return;
    }

    if (fmsSubMenu == FmsSubMenu.ViewPaymenHistory) {

      let link = `/accounting/acPaymentHistoryView`;

      switch (jobName) {
        case JobNames.OpInvoice:
        case JobNames.Invoice:
          fullLink = `${link}/INVO/${selectedElement.id}?parentOp=billings`;
          break;
        case JobNames.OpBill:
        case JobNames.Bill:
          fullLink = `${link}/BILL/${selectedElement.id}?parentOp=billings`;
          break;
        case JobNames.OpCrDb:
        case JobNames.CrDb:
          fullLink = `${link}/CRDB/${selectedElement.id}?parentOp=billings`;
          break;
        case JobNames.GInvo:
          fullLink = `${link}/GINVE/${selectedElement.id}?parentOp=billings`;
          break;
        case JobNames.GBill:
          fullLink = `${link}/GBILE/${selectedElement.id}?parentOp=billings`;
          break;
      }

      moveToSingleOpenedScreenInAnyOperationStartingWithUrl(fullLink, [fullLink]);
      return;
    }
   
    if (fmsSubMenu === FmsSubMenu.AutoBillingView) {
      setSelectedReportPopupMenuItem({
        label: 'Auto Billing',
        popupWidth: 860,
        popupComponent: (<AutoBilling  onPopupClose={() => setSelectedReportPopupMenuItem(null)} />)
      });
      return;
    }

    // if (fmsSubMenu == FmsSubMenu.AutoBillingView) {
    //  let link = `/accounting/acAutobilling?parentOp=billings`;
    //   switch (jobName) {
    //     case JobNames.GInvo:
    //       fullLink = `${link}`;
    //       break;
    //   }

    //   moveToSingleOpenedScreenInAnyOperationStartingWithUrl(fullLink, [fullLink]);
    //   return;
    // }
  }

  async function onReportMenuClick(fmsSubMenu: FmsSubMenu, model: any, jobName: JobNames) {
    setSelectedReportMenuItem(null);
    if (fmsSubMenu === FmsSubMenu.IssueReady) {
      const args = await getSelectedSubMenuArgs(fmsSubMenu, JobNames.RptIssueReady, null, appContext, showGlobalYesNoDialog, showGlobalOkDialog);
      if (args) {
        viewReport(args, "", true);
      }
      return;
    }
    const selectedElement = await getSelectedElement(model);
    if (!selectedElement) {
      return;
    }

    if (fmsSubMenu === FmsSubMenu.AesOcean || fmsSubMenu === FmsSubMenu.AesAir || fmsSubMenu === FmsSubMenu.AesOtherOP) {
      if (fmsSubMenu === FmsSubMenu.AesOcean) {
        if (!selectedElement.hblNo) {
          await showGlobalOkDialog(<p>No Hbl Number exist!</p>);
          return;
        }
      }
      else { 
        if (fmsSubMenu !== FmsSubMenu.AesOtherOP) {
          if (!selectedElement.hawbNo) {
            await showGlobalOkDialog(<p>No Hbl Number exist!</p>);
            return;
          }
        }
      }
      if (!(getFmsGlobal().PimsID)) {
        await showGlobalOkDialog(<p>CANNOT CONNECT DB SERVER!</p>);
        return;
      }

      var apiResult = await appContext.api.getAesLink();

      if (apiResult && apiResult.success) {
        window.open('https://aes.suntekscm.com/home/loginrequest/' + apiResult.result);
      }
      return;
    }

    let tempJobName = jobName;
    if (!tempJobName) { //Click moremenu in moremenu tab
      const params = history.location.search.replace('?', '').split('&');
      tempJobName = params.find(x => x.includes('jobname='))?.replace('jobname=', '') as JobNames;
      if (!tempJobName) //Click moremenu in operation detail tab
        tempJobName = opendOperationData?.jobName ?? JobNames.None;;
    }

    if(fmsSubMenu === FmsSubMenu.BTCNTRTracking){
      const l  = selectedElement;
      if(l.trType === 'M'){
        let link = `https://quicktrack2.suntekscm.com/?licid=${fmsGlobal.AcmeID}&mbl=${l.mblNo}&hbl=${l.hblNo}&cntr=${l.containerNo}&email=${appContext.user?.email}`;
        window.open(link);
      } else {
        await showGlobalOkDialog(<p>Can not show CNTR Tracking Info.</p>);
      }
      return;

    }

    const args = await getSelectedSubMenuArgs(fmsSubMenu, tempJobName, selectedElement, appContext, showGlobalYesNoDialog, showGlobalOkDialog);
    if (args) {
      viewReport(args, "", true);
    }
  }

  function closeGlobalOk() {
    if (globalOkDialog.resolve) {
      globalOkDialog.resolve();
    }
    dispatch(hideGlobalOkDialog());

    const currentCellSelection = document.querySelector('.data-cell-selected') as HTMLElement;
    if (currentCellSelection) {
      let parent: HTMLElement | null = currentCellSelection.parentElement;
      let tableId: string | null = null;
      let tableEle: HTMLElement | null = null;
      while (parent && !tableId) {
        if (parent.getAttribute('role') === 'table') {
          tableId = parent.id;
          tableEle = document.getElementById(tableId);
          break;
        }
        parent = parent.parentElement;
      }
      setFocusEleAfterModalOkBtnClick(tableEle);
    }
    else {
      const focusedElementIndexInStorage = fastStorage[`focusedEleIndexCurPageInStorage-${currentUrl}`];
      setFocusEleAfterModalOkBtnClick(getTabableElementByIndex(focusedElementIndexInStorage));
    }
  }

  function noMenus() {
    const hasJob = !!menus && !!menus.job && menus.job.length > 0;
    const hasDownload = !!menus && !!menus.download && menus.download.length > 0;
    const hasReport = !!menus && !!menus.report && menus.report.length > 0;
    const hasPreReport = !!menus && !!menus.preReport && menus.preReport.length > 0;
    const hasOperationReport = !!menus && !!menus.operationReport && menus.operationReport.length > 0;
    const hasMoreMenu = !!menus && !!menus.moreMenu && menus.moreMenu.length > 0;

    return !hasJob && !hasDownload && !hasReport && !hasPreReport && !hasOperationReport && !hasMoreMenu;
  }

  function getPopUpCompent(selectedPopupMenuItem: any, menuModel: any) {
    if (menuModel === undefined)
      return cloneElement(selectedPopupMenuItem.popupComponent!, { onPopupClose, updateSelectedPopupMenuItemWidth })
    else
      return cloneElement(selectedPopupMenuItem.popupComponent!, { onPopupClose, updateSelectedPopupMenuItemWidth, 
      selectedElement: async () => {const result = await getSelectedElement(menuModel); return result;}, 
      selectedElementId:  async () => {const result = await getSelectedElement(menuModel); return result.id;} })
  }

  if (!initialized) {
    return null;
  }

  function CheckMoreMenuJobName(moreMenuJobName: JobNames | undefined) {
    return currentListJobName === moreMenuJobName //ListView
      || history.location.search.includes(`jobname=${moreMenuJobName}`) //Moremenu Tab
      || (opendOperationData?.jobName === moreMenuJobName) //Operation detail tab
      || moreMenuJobName === JobNames.OtherOP;
  }

  function GetMoreMenuJobName(currentUrl: string) {
    const jobNameIndex = currentUrl.indexOf('jobname=');
    if (currentUrl.includes('&')) return currentUrl.substring(jobNameIndex + 8, currentUrl.indexOf('&', jobNameIndex));
    else return currentUrl.substring(jobNameIndex + 8);
  // return currentListJobName === moreMenuJobName //ListView
    //   || history.location.search.includes(`jobname=${moreMenuJobName}` ?? '') //Moremenu Tab
    //   || (opendOperationData?.jobName === moreMenuJobName); //Operation detail tab
  }
  // const systemMenus = _.cloneDeep(config.systemMenus);
  // if (!systemSettings.enableBranch) {
  //   if (systemMenus[0] && systemMenus[0].items && systemMenus[0].items[0]) {
  //     const originalItems = systemMenus[0].items[0].items ?? [];
  //     systemMenus[0].items[0].items = originalItems.filter(x => x.text !== 'Branch');
  //   }
  // }

  const onPopupClose = () => {
    setSelectedPopupMenuItem(null);
  }
  
  const onReportPopupClose = () => {
    setSelectedReportPopupMenuItem(null);
  }

  const updateSelectedPopupMenuItemWidth = (popupWidth: number) => {
    setSelectedPopupMenuItem((item) => {
      if(item) {
        item.popupWidth = popupWidth;
        return {...item}
      }
      return null;
    })
  }

  const hasSideMenu = !!menus && !noMenus() && !isHome;
  //const emptySideMenu = menus?.operation === 'Freight Receipt';
  const emptySideMenu = '';

  return (
    <div id="ifms-app" tabIndex={-1}>
      <JobSwitchingMenuContext.Provider value={{
        context: jobMenuContextState,
        setInContext: setInJobMenuContext,
        resetForCurrentJob: resetJobMenuContextState,
        isHome,
        hasSideMenu,
      }}>
        <div id="ifms-top-bar">
          <IfmsTopBar
            onMenuItemClicked={onMenuItemClicked}
            systemSettings={systemSettings}
            selectedOperation={operationState.sideMenu}
            menus={menus}
            currentUrl={`${history.location.pathname}${history.location.search}`}
            openNewPage={link => openNewPage(history, link, NavigationType.newPage)}
            logout={logoutUser}
            user={userLogin.user}
            showMainPop={showMainPop}
            setShowMainPop={setShowMainPop}
          />
          <JobSwitchingMenu testMode={jobSwitchingTestMode} resetJobMenuContextState={resetJobMenuContextState} />
        </div>
        <div id="ifms-app-route-container" style={{ height: `calc(100% - ${topBarHeight}px)`, top: `${topBarHeight}px`, display: 'flex' }}>
          {hasSideMenu &&
            <div id="side-menu-operations-parent" style={{ position: 'fixed', height: `calc(100% - ${topBarHeight}px)`, zIndex: 998 }}>
              <IfmsSideMenu
                parentId="side-menu-operations-parent"
                menuId="side-menu-operations"
                menus={menus}
                onMenuItemClicked={onMenuItemClicked}
                operation={operationState.sideMenu}
                topBarHeight={topBarHeight}
                opendOperationData={opendOperationData}
              />
            </div>
          }
          {emptySideMenu &&
            <div id="side-menu-operations-parent" style={{ position: 'fixed', height: `100%`, zIndex: 998 }}>
              <div className="ifms-side-menu-main">
                <div className="ifms-side-menu-operation-title">
                  {menus?.operation}
                </div>
                <div style={{ height: '14px' }}>
                  <div style={{ borderTop: '3px solid #dc3545', height: '2px', marginLeft: '22px', marginRight: '22px', position: 'relative', top: '6px' }}></div>
                </div>
              </div>

            </div>
          }
          <div style={{ marginLeft: `${IfmsSideMenuWidth}`, width: `calc(100% - ${IfmsSideMenuWidth})` }}>
            <div id="ifms-app-content" style={{ height: '100%' }}>
              <React.Suspense fallback={<div></div>}>
                {routes}
              </React.Suspense>
            </div>
            <div id="ifms-app-info-area">
              {/* {infoArea} */}
            </div>
          </div>
        </div>

        <Modal
          isOpen={globalYesNoCancelDialog.show}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          contentLabel=""
          style={{
            overlay: ModalOverlayStyle,
            content: ModalContentStyle
          }}
        >
          <div className="">
            {globalYesNoCancelDialog.content}
          </div>
          <ButtonGroup
            customClass="ifms-dialog-button"
            buttons={['Cancel', 'No', 'Yes']}
            onButton={b => {
              if (globalYesNoCancelDialog.resolve) {
                if (b === 'Yes') {
                  globalYesNoCancelDialog.resolve(YesNoCancelResult.Yes);
                } else if (b === 'No') {
                  globalYesNoCancelDialog.resolve(YesNoCancelResult.No);
                } else {
                  globalYesNoCancelDialog.resolve(YesNoCancelResult.Cancel);
                }
              }
              dispatch(hideGlobalYesNoCancelDialog());
            }}
            defaultFocusIndex={2}
          />
        </Modal>

        <Modal
          isOpen={globalYesNoDialog.show}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          contentLabel=""
          style={{
            overlay: ModalOverlayStyle,
            content: ModalDialogContentStyle
          }}
        >
          <div id='global-yesNoDialog' className="global-dialog-content" style={{ whiteSpace: 'pre' }}>
          <LeftRightSplit
                leftSize='40px'
                rightSize='calc(100% - 40px)'
                left={
                  <>
                    <img src={NoticeYesNoIcon} style={{ height: 32, width: 32, display: globalYesNoDialog.inform }} alt="" /> 
                    <img src={NoticeWarningIcon} style={{ height: 32, width: 32, display: globalYesNoDialog.warning }} alt="" /> 
                  </>
                }
                right={<div style={{marginTop:'4px'}}>
                  {globalYesNoDialog.content}</div>
                }
            />
          </div>
          <ButtonGroup
            customClass={(globalYesNoDialog.invert || globalYesNoDialog.addor) ? "ifms-dialog-button ifms-dialog-button-yesorno" : "ifms-dialog-button"}
            buttons={globalYesNoDialog.invert ? ['No', 'Yes'] : ['Yes', 'No']}
            onButton={b => {
              if (globalYesNoDialog.resolve) {
                if (b === 'Yes') {
                  globalYesNoDialog.resolve(true);
                } else {
                  globalYesNoDialog.resolve(false);
                }
              }
              dispatch(hideGlobalYesNoDialog());
            }}
            //defaultFocusIndex={globalYesNoDialog.focusindex > 0 ? globalYesNoDialog.focusindex : 0}
            defaultFocusIndex={globalYesNoDialog.focusindex}
            addOr={globalYesNoDialog.addor}
          />
        </Modal>
        <Modal
          isOpen={globalOkDialog.show}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          contentLabel=""
          style={{
            overlay: ModalOverlayStyle,
            content: globalOkDialog.top
              ? { ...ModalDialogContentStyle, top: globalOkDialog.top, transform: 'translate(-50%)', }
              : ModalDialogContentStyle
          }}
        >
          <div id='global-okDialog' className="global-dialog-content" style={{ whiteSpace: 'pre' }}>
            <LeftRightSplit
                leftSize='40px'
                rightSize='calc(100% - 40px)'
                left={
                  <div>
                  <img src={NoticeOKIcon} style={{ height: 32, width: 32, display: globalOkDialog.inform }} alt="" /> 
                  <img src={NoticeWarningIcon} style={{ height: 32, width: 32, display: globalOkDialog.warning }} alt="" /> 
                  <img src={NoticeErrorIcon} style={{ height: 32, width: 32, display: globalOkDialog.error }} alt="" /> 
                  </div>
                }
                right={
                <div style={{marginTop:'4px'}}>
                  {globalOkDialog.content}
                </div>
                }
            />
          </div>
          {!globalOkDialog.hideOkButton &&
            <OkButton
             // customClass="ifms-dialog-button"
              onOk={closeGlobalOk}
              customClass="ifms-dialog-button ifms-dialog-button-ok"
            />
          }
        </Modal>
        {apiLoading &&
          <div style={{
            position: 'fixed',
            top: '-4px',
            width: '100%',
            zIndex: 5000
          }}>
            <SkeletonTheme color="#4287f5" highlightColor="#b9d2fa">
              <Skeleton count={1} height={2} />
            </SkeletonTheme>
          </div>
        }
         {apiLoadingMessage &&
          <Modal
            isOpen={apiLoadingMessage}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            contentLabel=""
            style={{
              overlay: { ...ModalOverlayStyle, backgroundColor: 'rgba(0, 0, 0, 0.2)' },
              content: ModalContentStyle,
            }}
          >
            <div style={{ display: 'flex', height: '150px', width: '350px', justifyContent: 'center', alignItems: 'center', fontSize: '18px', }}>
              <Spinner /> The application is loading
            </div>
          </Modal>
        }

        {apiLoadingWithMessage !== null &&
          <Modal
            isOpen={apiLoadingWithMessage !== null}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            contentLabel=""
            style={{
              overlay: { ...ModalOverlayStyle, backgroundColor: 'rgba(0, 0, 0, 0.2)' },
              content: ModalContentStyle,
            }}
          >
            <div style={{ display: 'flex', height: '150px', width: '430px', justifyContent: 'center', alignItems: 'center', fontSize: '18px', }}>
              <Spinner /> {apiLoadingWithMessage}
            </div>
          </Modal>
        }

        <div id="ifms-app-left-drawer" style={{ position: 'fixed', bottom: '0' }}>
        </div>
        <div id="ifms-app-right-drawer" style={{ position: 'fixed', bottom: '0' }}>
        </div>
        <div id="ifms-app-bottom" style={{ position: 'fixed', bottom: '0', zIndex: 9999 }}>
        </div>
        <div id="ifms-app-poppers" style={{ position: 'fixed', bottom: '0', zIndex: 999999 }}>
        </div>

        {!!selectedPopupMenuItem ? (
          <IfmsModal
            isBlueHeader
            cancelOnEsc
            onCancel={onPopupClose}
            draggable
            isOpen={!!selectedPopupMenuItem}
            title={selectedPopupMenuItem?.popupLabel || selectedPopupMenuItem?.text}
            top={`${selectedPopupMenuItem?.popupTop ? selectedPopupMenuItem?.popupTop + 'px' : ''}`}
            width={`${selectedPopupMenuItem?.popupWidth || 760}px`}>
            {getPopUpCompent(selectedPopupMenuItem, currentListItemModel)}
          </IfmsModal>)
          : null
        }

        {!!selectedReportMenuItem && selectedReportMenuItem.subMenu ?
          onReportMenuClick(selectedReportMenuItem.subMenu, currentListItemModel, currentListJobName)
          : null
        }

        
        {!!selectedReportMenuItem && selectedReportMenuItem.subMenuNonReport ?
          onSubMenuClick(selectedReportMenuItem.subMenuNonReport, currentListItemModel, currentListJobName)
          : null
        }

        {!!selectedReportPopupMenuItem ? (
          <IfmsModal 
            isBlueHeader 
            cancelOnEsc 
            onCancel={onReportPopupClose} 
            draggable 
            top={`${selectedReportPopupMenuItem?.popupTop ? selectedReportPopupMenuItem?.popupTop + 'px' : ''}`}
            isOpen={!!selectedReportPopupMenuItem} 
            title={selectedReportPopupMenuItem?.popupLabel || selectedReportPopupMenuItem?.label} 
            width={`${selectedReportPopupMenuItem?.popupWidth || 540}px`}>
            {cloneElement(selectedReportPopupMenuItem.popupComponent!, { onReportPopupClose })}
          </IfmsModal>
        ) : null}

        {showMainPop && <MyMemo setShowMainPop={setShowMainPop} showMainPop={showMainPop}
          onClickEdit={(tbName: string, tbId: number, acctTbName: string) => {
            let jobName = getJobNameByTbName(tbName);
            let parentOpType = false;
            let parentOpName = '';
            if(jobName === JobNames.None){
              if(tbName === "T_DEPOHD"){
                if(acctTbName.substring(0,1) === 'D' && acctTbName.substring(2) === '1')
                  jobName = JobNames.AgntPayment;
                else if (acctTbName.substring(0,1) === 'D' && acctTbName.substring(2) !== '1')
                  jobName = JobNames.CustPayment;
                else if (acctTbName.substring(0,1) === 'M')
                  jobName = JobNames.CCPayment;
                else jobName = JobNames.BillPayment;
              }
              else {
                jobName = getJobNameFromInvBillCrdbTbName(tbName, acctTbName);
              }
            }
            let opName = getOpByjobName(jobName);
            let blType = '';
            switch (jobName) {
              case JobNames.OIMbl:
                blType = 'mbl';
                break;
              case JobNames.OIHbl:
                blType = 'hbl';
                break;
              case JobNames.OEMbl:
                blType = 'oeMbl';
                break;
              case JobNames.OEHbl:
                blType = 'oeHbl';
                break;
              case JobNames.AIMbl:
                blType = 'aiMawb';
                break;
              case JobNames.AIHbl:
                blType = 'aiHawb';
                break;
              case JobNames.AEMbl:
                blType = 'aeMawb';
                break;
              case JobNames.AEHbl:
                blType = 'aeHawb';
                break;
              case JobNames.OtherOP:
                blType = 'otherShipment';
                break;
              case JobNames.WHRcv:
                blType = 'whReceiving';
                break;
              case JobNames.WHShip:
                blType = 'whShipping';
                break;
              case JobNames.Company:
                blType = 'company';
                break;
              case JobNames.Invoice:
                blType = 'invoice';
                opName = getOpTypeFromInvBillCrdbTbName(acctTbName);
                if(opName === constants.opTypes.accounting){
                  parentOpName = 'billings';
                  parentOpType = true;
                }
                break;
              case JobNames.GInvo:
                blType = 'acGeneralInvoice';
                parentOpType = true;
                opName = constants.opTypes.accounting;
                parentOpName = 'billings';
                break;
              case JobNames.WHStorageInv:
                blType = 'whStorageInvoice';
                opName = constants.opTypes.warehousing;
                break;
              case JobNames.Bill:
                blType = 'bill';
                opName = getOpTypeFromInvBillCrdbTbName(acctTbName);
                if(opName === constants.opTypes.accounting){
                  parentOpName = 'billings';
                  parentOpType = true;
                }
                break;
              case JobNames.GBill:
                blType = 'acGeneralBill';
                parentOpType = true;
                opName = constants.opTypes.accounting;
                parentOpName = 'billings';
                break;
              case JobNames.CrDb:
                blType = 'crdb';
                opName = getOpTypeFromInvBillCrdbTbName(acctTbName);
                if(opName === constants.opTypes.accounting){
                  parentOpName = 'billings';
                  parentOpType = true;
                }
                break;
              case JobNames.CustPaymentMemo:
                blType = 'acCustomerPaymentMemo';
                opName = constants.opTypes.accounting;
                break;
              case JobNames.AgntPayment:
                blType = 'acAgentPayment';
                opName = constants.opTypes.accounting;
                break;
              case JobNames.CustPayment:
                blType = 'acCustomerPayment';
                opName = constants.opTypes.accounting;
                break;
              case JobNames.CCPayment:
                blType = 'acCCPayment';
                opName = constants.opTypes.accounting;
                break;
              case JobNames.BillPayment:
                blType = 'acBillPayment';
                opName = constants.opTypes.accounting;
                break;
              case JobNames.SeaQuotation:
                blType = 'oeQuotation';
                opName = constants.opTypes.oceanExport;
                parentOpType = true;
                parentOpName = 'oceanExport'
                break;
              case JobNames.BTOrder:
                blType = 'btOrder';
                opName =constants.opTypes.order;
                break;
              case JobNames.BTDo:
                blType = 'btLocalDO';
                opName = constants.opTypes.localDO;
                break;
              case JobNames.BTLoadPlan:
                blType = 'btLoadPlan';
                opName = constants.opTypes.load;
                break;
            }
            setFilterForScreenExternally(`/common/joblistview/${blType}/${opName}${parentOpType? `?parentOp=${parentOpName}`:''}`,
              'id', '', tbId.toString(), 'update', false, true,({...new SearchModelBase(), version: versionCount.current} as SearchModelBase));  // Update version);
              versionCount.current += 1;
            openNewPage(history, `/common/joblistview/${blType}/${opName}${parentOpType? `?parentOp=${parentOpName}`:''}`, NavigationType.newPage);
          }}
        />
        }
        {/*<pt220406: optab-rptviewer>
        <ReportViewer ref={reportRef} /> */}
      </JobSwitchingMenuContext.Provider>
      {DebugTableHeaderSizes &&
        <TableHeaderSizeDisplay />
      }

      {autoLogoutCheck &&
        <Modal
          isOpen={autoLogoutCheck}
          shouldCloseOnOverlayClick={false}
          shouldCloseOnEsc={false}
          contentLabel=""
          style={{
            overlay: ModalOverlayStyle,
            content: ModalContentStyle
          }}
        >
          <div id='global-okDialog' className="global-dialog-content" style={{ whiteSpace: 'pre', margin: '10px' }}>
            <LeftRightSplit
              leftSize='40px'
              rightSize='calc(100% - 40px)'
              left={
                <div>
                  <img src={NoticeWarningIcon} style={{ height: 32, width: 32, }} alt="" />
                </div>
              }
              right={
                <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                  You are about to be signed out due to inactivity.<br />
                  If there remains any unsaved data, it may be lost.
                </div>
              }
            />
          </div>

          <div style={{ display: 'flex' }}>

            <div style={{ marginLeft:'50px', marginRight: '20px' }}>
              Time Left - {`${Math.floor(autoLogoutRemain / 60)}`.padStart(2, '0')}:{`${autoLogoutRemain % 60}`.padStart(2, '0')}
            </div>

            <ButtonGroup
              customClass={"ifms-dialog-button ifms-dialog-button-yesorno"}
              buttons={['Stay Logged In', 'Logout']}
              defaultFocusIndex={0}
              addSpace
              onButton={b => {
                if (b === 'Stay Logged In') {
                  isStayLoggin.current = true;
                  stayLoggedin();
                } else {
                  logoutUser(false);
                }
              }}
            />
          </div>

        </Modal>
      }
    </div>
  );
}

export default IfmsMain;
